// #region Imports
import WorkVersionChip from "work/work-agreements/versions/_views/work-version-chip";
import ProposalPageContent from "work/proposals/_views/_shared/proposal-page-content";
import WorkVersion from "work/work-agreements/versions/work-version";
import PageTitleBreadcrumb, { breadcrumbHeadingName } from "_shared/_views/breadcrumbs/page-title-breadcrumb";
import CommentIcon from "@mui/icons-material/Comment";
import HistoryIcon from "@mui/icons-material/History";
import { isFetchBaseQueryError } from "_shared/_errors/api-error-message";
import useSessionPermissionsLoadState from "permissions/session/use-session-permissions-load-state";
import useSessionUser from "permissions/session/use-session-user";
import { useWhoHasAccess } from "permissions/_views/records/who-has-access";
import RecordAccessButton from "work/_views/record-access-button";
import { proposalListPath, ProposalStageChip } from "work/proposals/_views/proposals-page-tabs";
import { documentRefusalMessage } from "work/proposals/_views/drafts/document-refusal-message";
import {
  draftDeletionRefusalFromStatus,
  singleDraftDeletionRefusalMessage,
} from "work/proposals/draft-deletion/draft-deletion-result";
import DraftSaveRefusal from "work/proposals/_views/drafts/draft-save-refusal";
import ProposalSaveSplitButton from "work/proposals/_views/_buttons/proposal-save-split-button";
import ProposalSubmitSplitButton, {
  type ProposalSubmitOption,
} from "work/proposals/_views/_buttons/proposal-submit-split-button";
import useSaveAsProposalTemplateAction from "work/proposal-templates/_hooks/use-save-as-proposal-template-action";
import DeleteIcon from "@mui/icons-material/Delete";
import GroupsIcon from "@mui/icons-material/Groups";
import GroupAddIcon from "@mui/icons-material/GroupAdd";
import SendIcon from "@mui/icons-material/Send";
import ShortcutIcon from "@mui/icons-material/Shortcut";
import Alert from "@mui/material/Alert";
import WarningIcon from "@mui/icons-material/Warning";
import Badge from "@mui/material/Badge";
import { ThemeProvider, styled } from "@mui/material/styles";
import Tooltip from "@mui/material/Tooltip";
import { skipToken } from "@reduxjs/toolkit/query";
import { HttpStatusCode } from "axios";
import { NotAuthorized } from "_shared/_errors/_views/not-authorized";
import { ConfirmResponse, useConfirmDialog } from "_shared/_providers/dialogs/confirm/confirm-dialog";
import { RootState, useAttorneyHubDispatch } from "_shared/_stores/redux-store";
import {
  TraverserStopScopeContext,
  useTraverserStopScope,
} from "_shared/_views/highlights/traverser-stop-scope";
import Loader from "_shared/_views/loading/loader";
import EditFeeScheduleCategoryDrawer from "_shared/fee-schedules/categories/_views/templates/edit-fee-schedule-category-drawer";
import ExistingFeeScheduleCategoryDrawer from "_shared/fee-schedules/categories/_views/templates/existing-fee-schedule-category-drawer";
import FeeScheduleCategory from "_shared/fee-schedules/categories/fee-schedule-category";
import FeeScheduleCategoryName from "_shared/fee-schedules/categories/names/fee-schedule-category-name";
import {
  getFeeScheduleTeamMembers,
  getMarketplaceTeamId,
  withFeeScheduleStaffingSeated,
} from "_shared/fee-schedules/categories/fee-schedule-team-members";
import FeeScheduleTeamRoster from "_shared/fee-schedules/categories/fee-schedule-team-roster";
import { withoutRatesNamedOn } from "_shared/fee-schedules/categories/fee-schedule-template-category";
import { staffingForRates } from "_shared/fee-schedules/categories/fee-schedule-template-staffing";
import useReconcileFeeScheduleStaffing from "_shared/fee-schedules/categories/use-reconcile-fee-schedule-staffing";
import EntityMember from "_shared/entity-members/entity-member";
import Guid from "_shared/guids/guid";
import { chain, uniqBy } from "lodash";
import { useGetTeamByIdQuery, useGetTeamVendorsQuery } from "marketplace/teams/_api/marketplace-team-api";
import { getTeamById, getTeamVendors } from "marketplace/teams/_store/marketplace-teams-redux-slice";
import { enqueueSnackbar } from "_shared/_providers/snackbars/snackbars";
import { reportProposalMissingItemSaveFailure } from "work/proposals/_views/_shared/missing-item-save-failure-snackbar";
import { showActionableErrorSnackbar, showErrorSnackbar } from "_shared/_errors/error-snackbar";
import {
  PROPOSAL_CHANGED_MESSAGE,
  showProposalCommandError,
} from "work/proposals/_views/_shared/proposal-command-error";
import {
  HIDDEN_RATES_ADD_MESSAGE,
  HIDDEN_RATES_UPDATE_MESSAGE,
} from "work/proposals/_views/_shared/fee-schedule-hidden-rates";
import { getSessionPermissions } from "permissions/_stores/permissions-redux-slice";
import { ChangeEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router";
import { ConversationPane } from "messaging/conversations/conversation-links";
import useConversationPaneRequest from "messaging/conversations/use-conversation-pane-request";
import { useSession } from "users/__session/session-context";
import { AccountType } from "users/account-types/account-type";
import { FeeScheduleCategoryMaskedCurrency, Masked } from "work/masked-fields";
import {
  ButtonContainer,
  Content,
  ProposalTab,
  TabContent,
  TabsContainer,
} from "work/_views/work-page-styled-components";
import ProposalBuilder from "work/proposals/__builder/proposal-builder";
import "work/proposals/__extensions/users/user-extensions";
import {
  useCreateProposalMutation,
  useDeleteProposalMutation,
  useGiveReviewerApprovalMutation,
  useSaveProposalMutation,
} from "work/proposals/_api/proposal-api";
import { ProposalJSON } from "work/proposals/_schemas/proposal-schema";
import {
  getProposalIsNoLongerAvailable,
  clearNewProposalBuilder,
  clearLocalProposalById,
  clearProposalBuilderByProposalId,
  getOriginalProposalBuilderByProposalId,
  getUpdatedProposalBuilderByProposalId,
  isProposalLoadIssue,
  promoteCreatedProposalDraft,
  replaceOriginalProposalBuilder,
  replaceUpdatedProposalBuilder,
} from "work/proposals/_store/proposals-redux-slice";
import ProposalSecondaryActions, {
  type ProposalSecondaryAction,
} from "work/proposals/_views/_buttons/proposal-secondary-actions";
import SubmitReviewButton from "work/proposals/_views/_buttons/submit-review-button";
import {
  proposalFirmSendTerms,
  useSendProposalToFirmsMutation,
} from "work/proposals/bidding/_api/proposal-firm-send-api";
import SendToSeveralFirmsDialog from "work/proposals/bidding/_views/send-to-several-firms-dialog";
import { proposalComparisonPath } from "work/proposals/comparison/proposal-comparison";
import type { ProposalFirm } from "work/proposals/bidding/proposal-firm";
import {
  planProposalFirmSend,
  type ProposalFirmSendAttempt,
  recordProposalFirmSendFailure,
  recordProposalFirmSendResults,
} from "work/proposals/bidding/proposal-firm-send-attempt";
import {
  getProposalOperationDropWarning,
  getProposalSaveFailureDoing,
  getProposalSaveSuccessMessage,
  getSaveConfirmationCopy,
  proposalSaveChangeLogHeading,
} from "work/proposals/_views/_shared/proposal-save-confirmation-copy";
import type { ConfirmationMode } from "work/proposals/_views/_shared/proposal-save-confirmation-copy";
import ProposalChangeSummaryList from "work/proposals/_views/_shared/proposal-change-summary-list";
import {
  buildProposalSaveSharePlan,
  proposalSaveAudienceLabel,
  proposalSubmitRecipientMember,
} from "work/proposals/_views/_shared/proposal-change-share-plan";
import { useProposalChangeDescriptionContext } from "work/proposals/_views/_shared/use-proposal-change-description-context";
import type { ProposalChangeDescriptionContext } from "work/proposals/negotiation/proposal-change-description";
import {
  CommentButton,
  CommentButtonLabel,
  HighlightCommentButton,
  IssueTrackerFooter,
  IssueTrackerHeader,
  ProposalActions,
  ProposalPageActionButtons as ActionButtonsContainer,
  ProposalPageActionsContainer as PageActionsContainer,
  ProposalPageContainer as PageContainer,
  ProposalPageHeader as HeaderContainer,
  ProposalPageMain as Main,
  ProposalPageTitle as PageTitle,
  ProposalPageTitleContainer as TitleContainer,
  ProposalSidePanelColumn as SidePanelColumn,
  ProposalStickyPageHeader as StickyPageHeader,
  ProposalTabsBand,
} from "work/proposals/_views/_shared/proposal-styled-components";
import {
  getProposalDrawerWidth,
  NEW_FEE_CATEGORY_DRAWER_KEY,
  useProposalPageCanSeatSidePanel,
  useProposalPagePanels,
} from "work/proposals/_views/_shared/proposal-page-panels";
import { usePersistedProposalSaveOrchestration } from "work/proposals/_views/_shared/use-proposal-save-orchestration";
import useProposalTabPendingCounts from "work/proposals/_views/_shared/use-proposal-tab-pending-counts";
import useUnsavedProposalNavigationGuard from "work/proposals/_views/_shared/use-unsaved-proposal-navigation-guard";
import { navigationLeavesProposalEditor } from "work/proposals/_views/_shared/proposal-editor-paths";
import useAskBeforeUnload from "_shared/_views/use-ask-before-unload";
import useProposalPageAssistantScreenContext from "work/proposals/_views/_shared/use-proposal-page-assistant-screen-context";
import { paneFlushContent, paneIsCompact } from "work/_views/work-pane-layout";
import useWorkPaneTheme from "work/_views/work-pane-typography";
import useWorkPaneSize, {
  WorkPaneWidthContext,
} from "work/_views/use-work-pane-size";
import {
  isProposalAccessDeniedError,
  isProposalNoLongerAvailableError,
  PROPOSAL_ACCESS_DENIED_MESSAGE,
} from "work/proposals/_views/_shared/proposal-availability-error";
import {
  readProposalConflictResponse,
  useCompleteProposalReviewMutation,
  useHandOverProposalDraftMutation,
  useLazyGetProposalCollaborationQuery,
  useMarkProposalChangesSeenMutation,
  useSubmitProposalCollaborationDraftMutation,
} from "work/proposals/negotiation/proposal-collaboration-api";
import { describeProposalConflicts } from "work/proposals/negotiation/proposal-conflict-summary";
import { proposalFieldRestoreLockReason } from "work/proposals/negotiation/proposal-field-restore";
import {
  buildCompleteProposalReviewCommand,
  buildSubmitProposalDraftCommand,
  isBarredFromInitialProposalSetup,
} from "work/proposals/negotiation/proposal-command-builder";
import {
  proposalEnvelopeReceived,
  proposalOperationsQueued,
} from "work/proposals/negotiation/proposal-collaboration-redux-slice";
import { buildProposalSubmissionRevertOperations } from "work/proposals/negotiation/proposal-submission-revert";
import useSubmitProposalDialog, {
  CANCELED_SUBMISSION,
} from "work/proposals/_views/_shared/use-submit-proposal-dialog";
import { type TeamLeaderRoleAction } from "work/proposals/leader-handover/_views/team-leader-role-cell";
import useProposalSubmitRecipient from "work/proposals/_views/_shared/use-proposal-submit-recipient";
import { isReviewerApprovalCurrent } from "work/proposals/negotiation/proposal-reviewer-approval-state";
import { PageTab } from "work/proposals/_views/drafts/draft-proposal-page-tabs";
import ConflictsTab from "work/proposals/_views/drafts/tabs/draft-proposal-conflicts-tab";
import DetailsTab from "work/proposals/_views/drafts/tabs/draft-proposal-details-tab";
import SavedDraftCollections from "work/proposals/_views/drafts/saved-draft-collections";
import SavedDraftFields from "work/proposals/_views/drafts/saved-draft-fields";
import ClientTeamTab from "work/proposals/_views/drafts/tabs/draft-proposal-client-team-tab";
import DiscountTab from "work/proposals/_views/drafts/tabs/draft-proposal-discount-tab";
import FeeScheduleTab from "work/proposals/_views/drafts/tabs/draft-proposal-fee-schedule-tab";
import PoliciesTab from "work/proposals/_views/drafts/tabs/draft-proposal-policies-tab";
import TeamTab from "work/proposals/_views/drafts/tabs/draft-proposal-team-tab";
import { applyPublishedFeeScheduleToDraft } from "work/proposals/_views/drafts/tabs/apply-published-fee-schedule";
import PublishedRatesOffer, { publishedRatesOfferTeamId } from "work/proposals/_views/drafts/published-rates-offer";
import type PublishedFeeSchedule from "marketplace/teams/fee-schedules/fee-schedule";
import { proposalActivityCoverage } from "work/proposals/negotiation/proposal-activity";
import ProposalActivityPopover, { proposalActivityTitle } from "work/proposals/negotiation/proposal-activity-popover";
import {
  clearUnsavedCommentsByProposalId,
  getCommentsByProposal,
} from "work/proposals/comments/_store/comments-redux-slice";
import CommentsDrawer, { proposalCommentsTitle } from "work/proposals/comments/_view/comments-drawer";
import { offersNegotiation } from "work/proposals/comments/threads/offers-negotiation";
import { oldestUnreadCommentId } from "work/proposals/comments/oldest-unread-comment";
import { getConversationUnreadCount } from "work/proposals/comments/threads/conversation-unread-count";
import { useCommentPaneProposal } from "work/proposals/comments/comment-pane-proposal";
import {
  ProposalFieldHistoryRequestContext,
  ProposalFieldTraversalSource,
} from "work/proposals/negotiation/proposal-field-history-request-context";
import CommentThread from "work/proposals/comments/threads/comment-thread";
import {
  getCommentThreadsByProposal,
  populateCommentThreadsByProposal,
} from "work/proposals/comments/threads/_store/comment-thread-redux-slice";
import IssueTraverser, { IssueTraverserStopName } from "work/proposals/issues/_views/proposal-issue-traverser";
import { feeScheduleRowOrder } from "work/proposals/issues/proposal-traversal-stops";
import ProposalIssuesBadge from "work/proposals/issues/_views/proposal-issues-badge";
import { proposalFieldUnread, proposalUnreadChangeFields } from "work/proposals/negotiation/proposal-field-unread";
import {
  proposalTimelineItemPathForField,
  proposalTimelinePathForField,
} from "work/proposals/negotiation/proposal-field-timeline-path";
import { proposalTraversalKeepsCommentsPaneOpen } from "work/proposals/negotiation/proposal-traversal-comments-pane";
import { proposalFieldToMarkSeenOnTraversal } from "work/proposals/negotiation/proposal-field-traversal-seen";
import ProposalIssues, { type ProposalIssue } from "work/proposals/issues/proposal-issues";
import { revealFeeScheduleIssueCell } from "work/proposals/issues/fee-schedule-issue-cell";
import Proposal from "work/proposals/proposal";
import ProposalField, {
  ProposalFieldCategory,
  ProposalFieldCategoryType,
  ProposalFieldName,
} from "work/proposals/proposal-fields/proposal-field";
import ReviewerApprovalBadge from "work/proposals/reviewers/_views/reviewer-approval-badge";
import ReviewerLeaderMessage from "work/proposals/reviewers/_views/reviewer-leader-message";
import ReviewerSelector from "work/proposals/reviewers/_views/reviewer-selector";
import { useRequestProposalReReview } from "work/proposals/reviewers/_hooks/use-request-proposal-re-review";
import PendingReviewerReminders from "work/proposals/reviewers/_views/pending-reviewer-reminders";
import { pendingProposalReviewers } from "work/proposals/_views/_shared/proposal-pending-review-warning";
import { getReviewerActionState } from "work/proposals/reviewers/_views/reviewer-selection";
import ProposalReviewer from "work/proposals/reviewers/proposal-reviewer";
import FeeSchedule from "work/work-agreements/fee-schedules/fee-schedule";
import SelectedVendorTeamLeader from "work/work-agreements/teams/vendor-team/selection/selected-vendor-team-leader/selected-vendor-team-leader";
import SelectedVendorTeam from "work/work-agreements/teams/vendor-team/selection/selected-vendor-team/selected-vendor-team";
import { soleVendorMarketplaceTeam } from "work/work-agreements/teams/vendor-team/selection/sole-vendor-marketplace-team/sole-vendor-marketplace-team";
import VendorTeam from "work/work-agreements/teams/vendor-team/vendor-team";
import WorkAgreement from "work/work-agreements/work-agreement";
// #endregion

// #region Styled Components
const ProposalActionSpan = styled("span")(({ theme }) => ({
  maxWidth: "22rem",
  // As wide as its label on a comfortable pane, like every action in the row (#5973).
  minWidth: theme.spacing(15),
  width: "auto",
  [paneIsCompact(theme)]: {
    maxWidth: "none",
    minWidth: 0,
    width: "100%",
  },
}));
const NoBuilderLoader = styled(Loader)(({ theme }) => ({
  alignItems: "center",
  display: "flex",
  justifyContent: "center",
  height: "100%",
  padding: theme.spacing(2),
  width: "100%",
}));
// #endregion

const EMPTY_REVIEWERS: ProposalReviewer[] = [];
const EMPTY_COMMENT_THREADS: CommentThread[] = [];

// The traverser walks the fields a draft is not finished with, which on a draft means its issues: the
// values missing or invalid for submission, found by ProposalIssues. "Change" is the active
// proposal's word — it describes a counterparty's edit in a live negotiation, and a draft nobody has
// submitted yet has none, so on this page it named something that was never on screen.
const ISSUE_TRAVERSER_STOP_NAME: IssueTraverserStopName = "issue";

type DraftProposalAction = "edit" | "review" | "create" | "view";
const DraftProposalActions = new Set<DraftProposalAction>(["edit", "review", "create", "view"]);

/**
 * The draft with a rate's person on its vendor team, where the rate names somebody new to it.
 *
 * The Team Member field offers the vendor team first, and reaches anybody else only through
 * "Browse for new member" (#5798). Choosing somebody there means "add them to the team and to this
 * rate", so they are seated here without a question — a proposal may bill only its own vendor team,
 * and `WorkAgreement.ValidateFeeSchedule` refuses to hire otherwise. A rate that already named
 * somebody off the team, and is saved without changing them, adds nobody: it stays flagged.
 */
function withChosenTeamMemberSeated(
  builder: ProposalBuilder,
  teamMember: EntityMember | null | undefined,
  previousTeamMember: EntityMember | null | undefined,
): ProposalBuilder {
  const vendorTeam = builder.currentSpec.workAgreement?.vendorTeam;
  const newcomer = FeeScheduleTeamRoster.fromVendorTeam(vendorTeam).newcomerNamedBy(teamMember, previousTeamMember);
  if (!newcomer) return builder;
  const currentVendorTeam = Masked.isPresentNotMasked(vendorTeam) ? vendorTeam : new VendorTeam({ members: [] });
  return builder.setVendorTeam(withFeeScheduleStaffingSeated(currentVendorTeam, [newcomer]));
}

const NO_SAVE_REFUSAL_ISSUES: readonly ProposalIssue[] = [];

/** A save the page holds back because the draft it would write to never loaded (#6614). */
const UNLOADED_PROPOSAL_SAVE_MESSAGE =
  "This proposal could not be loaded, so your changes can't be saved. Reload the page to load it again.";

/** The draft's own issues, and those of the last save the API refused while it still applies. */
function withSaveRefusalIssues(issues: ProposalIssues, refusalIssues: readonly ProposalIssue[]): ProposalIssues {
  return refusalIssues.length > 0 ? issues.including(refusalIssues) : issues;
}

/**
 * What the snackbar says when a save fails: the API's own sentence when it sent one the page may
 * show, else the generic one.
 *
 * A draft the API refuses to create says why, and what to do (#5658): its creator holds no seat on
 * it, its leader cannot sign, or one person leads both teams. The generic text left the user with
 * nothing to act on. Only an HTTP failure is read, because a thrown `Error` carries this page's own
 * wording, not a sentence for the user. The shared `validation_failed` sentence is not used either:
 * it asks the user to "correct the highlighted fields", and it names no field this page highlights.
 * A team refusal's fields are highlighted, by `DraftSaveRefusal`.
 *
 * At module scope so the page's save routine is not asked to compile it. See app/CLAUDE.md.
 */

/**
 * Says why the page's delete failed: the reason, in the words the list uses for the same refusal
 * (#6577), or, for a status that is not a refusal, that support was notified (#6614).
 *
 * At module scope so the page's delete routine is not asked to compile it. See app/CLAUDE.md.
 */
function showDeleteFailure(error: unknown): void {
  const refusal = isFetchBaseQueryError(error) ? draftDeletionRefusalFromStatus(error.status) : undefined;
  showErrorSnackbar(error, {
    doing: "deleting the draft",
    operation: "proposal.delete-draft",
    message: refusal ? singleDraftDeletionRefusalMessage(refusal) : undefined,
  });
}

export default function DraftProposalPage() {
  // #region Hooks
  const location = useLocation();
  const session = useSession();
  const navigate = useNavigate();
  const { action: actionParam, id: idParam, pageTab: pageTabParam } = useParams();
  const dispatch = useAttorneyHubDispatch();
  const confirm = useConfirmDialog();
  const reconcileStaffing = useReconcileFeeScheduleStaffing();
  const [searchParams] = useSearchParams();
  // What narrows the controls is the pane, not the window: the navigation drawer and this page's own
  // right-hand drawer both take width a media query cannot see (#4964).
  const { paneRef, paneWidth, isCompact: paneIsNarrow } = useWorkPaneSize();
  const paneTheme = useWorkPaneTheme(paneWidth);
  const canSeatSidePanel = useProposalPageCanSeatSidePanel();

  const pageAction: DraftProposalAction = (actionParam as DraftProposalAction) ?? "create";

  // Unsupported action
  if (!DraftProposalActions.has(pageAction)) {
    throw new Response("Not Found", { status: HttpStatusCode.NotFound });
  }
  // Proposal id is required for edit/review
  if (pageAction !== "create" && !idParam) {
    throw new Response("Not Found", { status: HttpStatusCode.NotFound });
  }

  // #region State
  const [panelState, dispatchPanelState] = useProposalPagePanels<
    FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency
  >();
  const {
    selectedReviewField,
    commentsTitle: commentsPanelTitle,
    commentsOpen: commentsPanelIsOpen,
    addFeeOpen: isAddFeePanelOpen,
    existingFeeOpen: isExistingFeePanelOpen,
    teamSelectionOpen: isTeamSelectionPanelOpen,
    currentCategory,
    reviewerAnchor: reviewerSelectorAnchor,
    isManagingReviewers,
    traversalStop,
    commentToScrollTo,
    fieldArrivals,
  } = panelState;

  const [isSaving, setIsSaving] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  // The submission decision — what to send, to whom, and whether to publish comments — is asked once,
  // against the envelope the pre-submit save just acknowledged, so the dialog is opened mid-submit and
  // awaited through this resolver.
  const { resolveSubmission, resolveUnsavedDraftSubmission, submitProposalDialog } = useSubmitProposalDialog();
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const [isSelectingClient, setIsSelectingClient] = useState<boolean>(false);
  // The vendor closed the published-rates offer on this draft (#6114, #6468).
  const [publishedRatesOfferDismissed, setPublishedRatesOfferDismissed] = useState(false);
  const [createdProposalIdAwaitingHydration, setCreatedProposalIdAwaitingHydration] = useState<string>();
  const [newFeeCategoryFormKey, setNewFeeCategoryFormKey] = useState(NEW_FEE_CATEGORY_DRAWER_KEY);
  // #endregion

  // #region Globals
  const proposalId = useMemo(() => (idParam ? new Guid(idParam) : undefined), [idParam]);
  const preSelectedLeaderInfo = SelectedVendorTeamLeader.fromURLSearchParams(searchParams);
  const preSelectedTeamInfo = SelectedVendorTeam.fromURLSearchParams(searchParams);
  const preSelectedMarketplaceTeam = useSelector(getTeamById(preSelectedTeamInfo?.targetTeamId));
  const preSelectedMarketplaceTeamQuery = useGetTeamByIdQuery(
    preSelectedTeamInfo?.targetTeamId ? { teamId: preSelectedTeamInfo.targetTeamId.value } : skipToken,
  );
  const existingWorkAgreement: WorkAgreement = location.state?.workAgreement;
  // A vendor's new draft takes the vendor's marketplace team when there is exactly one, so the Fee
  // Schedule tab can offer that team's published rates (#6468). A team named in the URL wins.
  const seatsVendorsOwnTeam = Boolean(
    !proposalId && !preSelectedTeamInfo && !existingWorkAgreement && session.context?.viewingAsVendor,
  );
  const vendorsOwnTeamsQuery = useGetTeamVendorsQuery(seatsVendorsOwnTeam ? undefined : skipToken);
  const vendorsOwnTeams = useSelector(getTeamVendors());
  const vendorsSoleTeam =
    seatsVendorsOwnTeam && session.user
      ? soleVendorMarketplaceTeam(vendorsOwnTeams, session.user.legalEntityId)
      : undefined;
  const isReadOnlyView = pageAction === "view" || !pageAction;
  const activeTab = pageTabParam as PageTab;
  const isTeamSelectionDrawerOpen = activeTab === PageTab.VendorTeam && isTeamSelectionPanelOpen;

  // #region Permissions Queries
  const sessionPermissions = useSelector(getSessionPermissions);
  const permissionsLoadState = useSessionPermissionsLoadState();
  const sessionUser = useSessionUser();
  const proposalAccess = useWhoHasAccess("proposals", "the proposal");

  // #region Proposal Queries
  const collaboration = usePersistedProposalSaveOrchestration(proposalId?.value, { isDeleting });
  // Deleted, or this viewer's access revoked, while they had it open — pushed over the websocket.
  const proposalWasDeleted = useSelector(getProposalIsNoLongerAvailable(proposalId));
  const proposalJson = collaboration.mode === "canonical" ? collaboration.envelope?.projection : undefined;
  const effectiveProposalJson =
    collaboration.mode === "canonical" ? collaboration.effectiveProposalJSON : proposalJson;
  const proposal = useMemo(() => {
    if (!proposalJson || isProposalLoadIssue(proposalJson) || !session.user) return null;
    return Proposal.fromJSON({
      json: proposalJson,
      currentUser: session.user,
    });
  }, [proposalJson, session.user]);
  // What each field's Undo returns to: the draft as last saved. A draft never saved has no saved value,
  // so it has none and shows no Undo (#6873).
  const savedDraftFields = useMemo(
    () => (proposal?.id ? new SavedDraftFields(proposal.spec) : undefined),
    [proposal],
  );
  // What each collection row's Undo returns to, on the same terms (#6995).
  const savedDraftCollections = useMemo(
    () => (proposal?.id ? new SavedDraftCollections(proposal.spec) : undefined),
    [proposal],
  );
  const effectiveProposal = useMemo(() => {
    if (!effectiveProposalJson || isProposalLoadIssue(effectiveProposalJson) || !session.user) return null;
    return Proposal.fromJSON({
      json: effectiveProposalJson,
      currentUser: session.user,
    });
  }, [effectiveProposalJson, session.user]);
  // The saved draft, never the edits on this page: a template holds what was saved (#6129).
  const saveAsProposalTemplate = useSaveAsProposalTemplateAction({
    proposal,
    canCreateProposals: sessionUser?.canCreateProposals() ?? false,
  });

  // #region Proposal Builder Queries
  const originalBuilderJSON = useSelector(getOriginalProposalBuilderByProposalId(proposalId ?? Guid.EMPTY));
  const legacyOriginalBuilder = useMemo(() => {
    return ProposalBuilder.fromJSON({ json: originalBuilderJSON, session });
  }, [originalBuilderJSON, session]);

  const updatedBuilderJSON = useSelector(getUpdatedProposalBuilderByProposalId(proposalId ?? Guid.EMPTY));
  const legacyUpdatedBuilder = useMemo(() => {
    return ProposalBuilder.fromJSON({ json: updatedBuilderJSON, session });
  }, [updatedBuilderJSON, session]);
  const canonicalOriginalBuilder = useMemo(
    () =>
      collaboration.mode === "canonical" && proposal
        ? new ProposalBuilder({ session, startingSpec: proposal.spec })
        : null,
    [collaboration.mode, proposal, session],
  );
  const canonicalUpdatedBuilder = useMemo(
    () =>
      collaboration.mode === "canonical" && effectiveProposal
        ? new ProposalBuilder({ session, startingSpec: effectiveProposal.spec })
        : null,
    [collaboration.mode, effectiveProposal, session],
  );
  const canUseLegacyEditor = collaboration.mode === "disabled";
  const originalBuilder =
    collaboration.mode === "canonical"
      ? canonicalOriginalBuilder
      : canUseLegacyEditor
        ? legacyOriginalBuilder
        : null;
  const updatedBuilder =
    collaboration.mode === "canonical"
      ? canonicalUpdatedBuilder
      : canUseLegacyEditor
        ? legacyUpdatedBuilder
        : null;

  // The last save the API refused, kept on the fields it names until the user changes them.
  const [saveRefusal, setSaveRefusal] = useState<DraftSaveRefusal>();
  const saveRefusalIssues = useMemo(
    () => (saveRefusal?.appliesTo(updatedBuilder) ? saveRefusal.issues : NO_SAVE_REFUSAL_ISSUES),
    [saveRefusal, updatedBuilder],
  );
  const updatedBuilderIssues = useMemo(() => {
    const builderIssues = updatedBuilder ? ProposalIssues.fromBuilder(updatedBuilder) : new ProposalIssues();
    return withSaveRefusalIssues(builderIssues, saveRefusalIssues);
  }, [updatedBuilder, saveRefusalIssues]);
  const updatedBuilderIssuesRef = useRef<ProposalIssues>(updatedBuilderIssues);
  useEffect(() => {
    updatedBuilderIssuesRef.current = updatedBuilderIssues;
  }, [updatedBuilderIssues]);

  // #region Comments Queries
  // Comments are Messaging-native: a comment is a Message in a topic-filtered Forum keyed by the
  // durable negotiationId. We feed the comment layer a proposal whose id is that negotiationId so the
  // forum/thread/store keys align on it. Reading this store (also fed by the `messages` Ably
  // middleware) is what restores comment realtime. Drafts stay author-only and client-side pending
  // until Save publishes them to the Forum subscribers.
  const negotiationGuid = useMemo(() => {
    const id = collaboration.mode === "canonical" ? collaboration.envelope?.negotiationId : undefined;
    if (!id) return undefined;
    try {
      return new Guid(id);
    } catch {
      return undefined;
    }
  }, [collaboration.mode, collaboration.envelope?.negotiationId]);
  // Held through an envelope reload rather than re-derived on every render: the pane is rendered only
  // while this is present, and the composer inside it loses what is being typed if it is unmounted and
  // remounted underneath. See `useCommentPaneProposal`.
  const commentProposal = useCommentPaneProposal({
    routeProposalId: proposalId,
    proposal,
    negotiationId: collaboration.mode === "canonical" ? collaboration.envelope?.negotiationId : undefined,
    envelopeIsLoading: collaboration.isLoading,
  });
  // Whether there is a Comments pane to show at all. A draft that has never been persisted has
  // no negotiation behind it, so the pane renders nothing — and anything that "opens" it then reserves
  // the pane's width beside a pane that was never drawn, which is the blank column on the right of the
  // page. One flag decides all three: whether the drawer renders, whether its width is reserved, and
  // whether a caller may open it.
  const commentsPaneIsAvailable = Boolean(commentProposal?.createdDate);
  // The field whose change history the change traverser asked to open, published to the tabs by
  // context so the traverser can reach a control that lives inside whichever tab renders the field.
  const [fieldHistoryToOpen, setFieldHistoryToOpen] = useState<ProposalField | undefined>(undefined);
  // The fee schedule's categories as its table draws them, sorted as the reader left it; undefined while
  // the table is not drawn (#6617).
  const [displayedFeeScheduleCategoryIds, setDisplayedFeeScheduleCategoryIds] = useState<
    readonly Guid[] | undefined
  >(undefined);
  const fieldHistoryRequest = {
    field: fieldHistoryToOpen,
    onHonored: () => setFieldHistoryToOpen(undefined),
  };
  const loadedCommentThreads = useSelector(getCommentThreadsByProposal(commentProposal));
  const commentThreads = loadedCommentThreads ?? EMPTY_COMMENT_THREADS;
  // Each conversation's count, as its tab in the pane shows it: the traverser stops once at each one
  // with any (#5824). The Comments button shows both together. A draft's pane offers no negotiation
  // (#6515), so an unread comment there, written before the pane stopped offering it, is neither
  // counted nor a stop: the reader could never open it to read it.
  const negotiationIsOffered = offersNegotiation(commentProposal ?? proposal);
  const allStakeholdersUnreadCount = useSelector(
    getConversationUnreadCount({
      commentThreads,
      proposalId: commentProposal?.id ?? undefined,
      isExternal: true,
      readerId: session.user?.id,
    }),
  );
  const unreadCommentCounts = {
    allStakeholders: negotiationIsOffered ? allStakeholdersUnreadCount : 0,
    ownTeam: useSelector(
      getConversationUnreadCount({
        commentThreads,
        proposalId: commentProposal?.id ?? undefined,
        isExternal: false,
        readerId: session.user?.id,
      }),
    ),
  };
  const unreadCommentCount = unreadCommentCounts.allStakeholders + unreadCommentCounts.ownTeam;
  // Selected as ids rather than as the comment list, so the page renders again when a conversation's
  // oldest unread comment changes and not on every write to it. One per conversation: each is a stop.
  const oldestUnreadNegotiationComment = useSelector((state: RootState) =>
    oldestUnreadCommentId(getCommentsByProposal(commentProposal?.id)(state), session.user?.id, true),
  );
  const oldestUnreadAllStakeholdersComment = negotiationIsOffered ? oldestUnreadNegotiationComment : undefined;
  const oldestUnreadOwnTeamComment = useSelector((state: RootState) =>
    oldestUnreadCommentId(getCommentsByProposal(commentProposal?.id)(state), session.user?.id, false),
  );
  // Resolves people and collection items for every change description this page renders — the save
  // confirmation, the submit dialog, and the field history all read from the same context.
  const changeDescriptionContext = useProposalChangeDescriptionContext(
    effectiveProposalJson ?? proposalJson,
    collaboration.envelope?.projection,
    collaboration.envelope?.changeLines,
  );
  const isLoading = collaboration.isLoading;

  const [createProposalQuery] = useCreateProposalMutation();
  const [saveProposalQuery, saveProposalResult] = useSaveProposalMutation();
  const [loadProposalCollaboration] = useLazyGetProposalCollaborationQuery();
  const [submitProposalCollaborationDraft] = useSubmitProposalCollaborationDraftMutation();
  const [markProposalChangesSeen] = useMarkProposalChangesSeenMutation();
  const [completeProposalReview, completeProposalReviewResult] = useCompleteProposalReviewMutation();
  const [handOverProposalDraft, handOverProposalDraftResult] = useHandOverProposalDraftMutation();
  const [deleteProposalQuery] = useDeleteProposalMutation();
  const [sendProposalToFirms] = useSendProposalToFirmsMutation();
  const [isFirmSendOpen, setIsFirmSendOpen] = useState(false);
  // One command id per send of the same terms: a retry resends it, so no firm is drafted twice (#576).
  const firmSendAttemptRef = useRef<ProposalFirmSendAttempt | undefined>(undefined);
  const [, giveReviewerApprovalQueryResult] = useGiveReviewerApprovalMutation();

  const isReviewing = useMemo(() => {
    return (
      [...(proposal?.clientReviewers ?? []), ...(proposal?.vendorReviewers ?? [])].some((reviewer) =>
        reviewer.userId.isEqualTo(session.user?.id),
      ) ?? false
    );
  }, [proposal, session.user]);
  const isViewingAsVendor = session.context?.viewingAsVendor;
  // The header's Activity popover (#5648). This page draws only the viewer's own party's policies, and
  // the leader's row only while the team has a leader, so the rest of those histories are activity.
  const [activityAnchor, setActivityAnchor] = useState<HTMLElement | null>(null);
  const activityVendorTeam = updatedBuilder?.currentSpec.workAgreement?.vendorTeam;
  const hasVisibleTeamLeader = Boolean(Masked.isPresentNotMasked(activityVendorTeam) && activityVendorTeam.leader);
  const activityCoverage = useMemo(
    () =>
      proposalActivityCoverage({
        hasTeamLeader: hasVisibleTeamLeader,
        policyParties: isViewingAsVendor ? "vendor" : "client",
      }),
    [hasVisibleTeamLeader, isViewingAsVendor],
  );
  const vendorReviewers = updatedBuilder?.currentSpec.vendorReviewers;
  const clientReviewers = updatedBuilder?.currentSpec.clientReviewers;
  const reviewers = (isViewingAsVendor ? vendorReviewers : clientReviewers) ?? EMPTY_REVIEWERS;
  // Who Submit reaches, read from the editor so a draft nobody has saved can name them too, and looked
  // up as the page renders so the dialog has the person and firm the moment it opens (#5813).
  // The team leader a hand-over reaches, named on the control and in the confirmation (#6579).
  const primaryNegotiator = useProposalSubmitRecipient(proposal?.primaryNegotiator);
  const primaryNegotiatorName = primaryNegotiator?.fullName.trim() || "your team leader";
  const submitRecipient = useProposalSubmitRecipient(
    proposalSubmitRecipientMember(updatedBuilder?.currentSpec.workAgreement, isViewingAsVendor ? "Vendor" : "Client"),
  );
  // What each tab is holding that has not reached its audience yet: this actor's own unsaved changes.
  // Who a save reaches decides the words, so this sits after the reviewers it reads (#5812). Distinct
  // from the unviewed badge beside it, which counts what somebody else did.
  const tabPendingCounts = useProposalTabPendingCounts({
    pendingOperations: collaboration.pendingChanges.operations,
    reviewerCount: reviewers.length,
    isReviewing,
  });
  useProposalPageAssistantScreenContext({
    page: "Draft proposal",
    proposal,
    tab: activeTab,
    openPanel: panelState.openSidePanel,
    commentsTitle: commentsPanelTitle,
    action: pageAction,
    unsavedChanges: { count: tabPendingCounts.unsavedChangeCount, status: tabPendingCounts.unsavedChangeStatus },
  });
  const reviewerBaseline =
    (isViewingAsVendor ? proposal?.vendorReviewers : proposal?.clientReviewers) ?? EMPTY_REVIEWERS;
  const updatedBuilderWithSavedReviewers = useMemo(() => {
    if (!proposal || !updatedBuilder) return updatedBuilder;
    return updatedBuilder.setClientReviewers(proposal.clientReviewers).setVendorReviewers(proposal.vendorReviewers);
  }, [proposal, updatedBuilder]);
  const builderDiffersFromOriginal = useMemo(
    () => Boolean(updatedBuilder?.isDifferentFrom(originalBuilder)),
    [originalBuilder, updatedBuilder],
  );
  const hasPendingNonReviewerBuilderChanges = useMemo(
    () => Boolean(proposal?.isModifiedByBuilder(updatedBuilderWithSavedReviewers)),
    [proposal, updatedBuilderWithSavedReviewers],
  );
  const hasPendingReviewerChanges =
    collaboration.mode === "canonical"
      ? collaboration.hasPendingChanges
      : Boolean(proposal?.isModifiedByBuilder(updatedBuilder));
  const reviewCompletionSharePlan = buildProposalSaveSharePlan({
    operations: collaboration.pendingChanges.operations,
    reviewerChanges: collaboration.pendingChanges.reviewerChanges,
    audienceLabel: proposalSaveAudienceLabel({
      reviewerCount: reviewers.length,
      isReviewing,
    }),
    context: changeDescriptionContext,
  });
  const reviewerHasCurrentApproval = (reviewer: ProposalReviewer) =>
    collaboration.mode === "canonical" && collaboration.envelope
      ? isReviewerApprovalCurrent(collaboration.envelope, {
          userId: reviewer.userId.value,
          legalEntityId: reviewer.entityId.value,
        })
      : Boolean(reviewer.dateApproved);
  const allReviewers: ProposalReviewer[] = [
    ...(proposal?.clientReviewers ?? []),
    ...(proposal?.vendorReviewers ?? []),
  ];
  const hasUserApprovedReview = allReviewers.some(
    (reviewer) => reviewer.userId.isEqualTo(session.user?.id) && reviewerHasCurrentApproval(reviewer),
  );
  const reviewCompletionLocksEditing = collaboration.mode !== "canonical" && hasUserApprovedReview;
  const canEditProposal =
    collaboration.mode === "canonical"
      ? Boolean(collaboration.capabilities?.canEditDraft)
      : !proposal || Boolean(session.user?.canEdit(proposal));
  // Editing the proposal and configuring its initial setup are two permissions, not one. A reviewer
  // granted edit rights may change the Vendor Team's ROSTER; its IDENTITY — the leader and the
  // marketplace team — stays with the creator until first submission. Offering the leader controls
  // on `canEditDraft` alone let a reviewer make a change the save path then dropped in silence, so
  // the team came back with new members under the old leader (#4854).
  const canChangeVendorTeamIdentity = !isBarredFromInitialProposalSetup(collaboration.capabilities);
  const reReview = useRequestProposalReReview({
    envelope: collaboration.mode === "canonical" ? collaboration.envelope : undefined,
    viewerScope: collaboration.viewerScope,
    canManageReviewers:
      collaboration.mode === "canonical" ? Boolean(collaboration.capabilities?.canManageReviewers) : false,
  });


  const issues = useMemo(
    () => withSaveRefusalIssues(ProposalIssues.fromBuilder(updatedBuilder ?? undefined), saveRefusalIssues),
    [updatedBuilder, saveRefusalIssues],
  );
  // Sending an opening offer needs entity-wide access, a higher bar than drafting one (#5795). A
  // counter on a proposal already sent, or a revision of a hired agreement, keeps the old rules, as
  // it does on the API. Said on the button rather than met as a 403 after clicking it.
  const isOpeningOffer = !proposal?.isSubmitted && !proposal?.revisesHiredWorkAgreement;
  const mayNotSendOpeningOffer = isOpeningOffer && !sessionUser?.canSendNewProposals();
  // Only a draft's owner submits it: its creator, or the primary negotiator they handed it over to,
  // whatever their seat on it (#6579). A draft nobody has saved is its creator's. A proposal the
  // server names no owner for keeps the old rules: its creator owns it, and a reviewer does not submit.
  const draftOwner = proposal?.owner;
  const viewerOwnsDraft = draftOwner
    ? draftOwner.userId.isEqualTo(session.user?.id)
    : !proposal || Boolean(proposal.creator?.userId.isEqualTo(session.user?.id));
  const showsSubmit = !isReadOnlyView && (draftOwner ? viewerOwnsDraft : !isReviewing);
  const canHandOver = collaboration.mode === "canonical" && Boolean(collaboration.capabilities?.canHandOver);
  const viewerHandedDraftOver = Boolean(proposal?.draftHandedOverAt) && Boolean(draftOwner) && !viewerOwnsDraft;
  const issueFields = useMemo(() => {
    return uniqBy(issues?.entries.map((issue) => issue.field) ?? [], (field) => field.toJSON());
  }, [issues]);

  const proposalRef = useRef(proposal);
  // The proposal this editor has already created, held apart from `proposalRef` because the sync
  // effect below rewrites that one on EVERY render (its `collaboration` dependency is a fresh object
  // each time) — so the "I already created it" memory it used to carry survived only until the next
  // paint, and any save entered in the window before the saved route hydrates took the create branch
  // a second time. This ref is written exactly once, where the create resolves, and never cleared by
  // rendering.
  const createdProposalRef = useRef<Proposal | undefined>(undefined);
  // One save at a time. Checked and set synchronously — before `setIsSaving`, before any await — so
  // re-entrancy is impossible regardless of when React commits. The header buttons are gated by
  // `disableSave`, but the discard-changes prompt's "Save & Close" is not, and that is the path a
  // reviewer edit ends on.
  const saveInFlightRef = useRef<Promise<string | null | undefined> | null>(null);
  // The idempotency key for creating this draft: minted once for the editing session and resent
  // unchanged on every attempt, so a create that reaches the server twice resolves to one proposal.
  // A fresh uuid per attempt would guard nothing.
  const draftCreationCommandIdRef = useRef<string>(crypto.randomUUID());
  const collaborationRef = useRef(collaboration);
  const updatedBuilderRef = useRef(updatedBuilder);
  // The update handler of the latest render, for work that finishes after a dialog: its closure holds
  // the projection and the collaboration as they were when it was drawn.
  const handleProposalBuilderUpdatedRef = useRef(handleProposalBuilderUpdated);
  useEffect(() => {
    handleProposalBuilderUpdatedRef.current = handleProposalBuilderUpdated;
  });
  // `handleSaveChanges` is memoized on `[saveProposalResult.error]` alone, so its closure is effectively
  // frozen at first render — when nobody has been fetched and no team has been chosen yet. Reading the
  // context through a ref is what lets the save confirmation describe the page as it is NOW; without it
  // every change that needs a person's name ("Set Team Leader", "Added Team Member") rendered nameless.
  const changeDescriptionContextRef = useRef<ProposalChangeDescriptionContext>({});
  const builderStorageIdRef = useRef(proposalId ?? Guid.EMPTY);
  const isDeletingProposalRef = useRef(false);
  const isSubmittingProposalRef = useRef(false);
  const appDirectedNavigationPathRef = useRef<string | null>(null);
  const networkRequestIsInFlight = isLoading || isSaving || isSubmitting || completeProposalReviewResult.isLoading;
  const createdProposalRouteIsHydrated = Boolean(
    createdProposalIdAwaitingHydration &&
      proposalId?.value === createdProposalIdAwaitingHydration &&
      proposal?.id?.value === createdProposalIdAwaitingHydration &&
      updatedBuilder,
  );
  const createdProposalRouteIsTransitioning = Boolean(
    createdProposalIdAwaitingHydration &&
      (!proposalId || proposalId.value === createdProposalIdAwaitingHydration) &&
      !createdProposalRouteIsHydrated,
  );
  const pageControlsArePaused = networkRequestIsInFlight || createdProposalRouteIsTransitioning;
  const feeScheduleEditingIsDisabled = Boolean(
    isReadOnlyView || pageControlsArePaused || reviewCompletionLocksEditing || !canEditProposal,
  );

  // Whether the proposal's source can still change its answer. An error is a settled answer, not a
  // pending one — but only when it is terminal: a deleted draft answers 404 and the viewer must be sent
  // back, while a transient failure must leave them where they are rather than ejecting them mid-edit.
  //
  // Derived out here rather than inside the effect below so the effect depends on this one boolean
  // instead of three fields of a re-created object — including `collaboration.error`, which the effect
  // read while the dependency list did not name it.
  const proposalSourceIsResolving =
    collaboration.isLoading ||
    (collaboration.mode === "error" && !isProposalNoLongerAvailableError(collaboration.error));

  // A refusal is not an absence. Derived beside the boolean above, and for the same reason: the effect
  // below names its own dependencies, so it must not read `collaboration.error` itself.
  const proposalAccessWasDenied = collaboration.mode === "error" && isProposalAccessDeniedError(collaboration.error);

  // #region Lifecycle
  useEffect(() => {
    let submittedProposalAction: "review" | "revise" | "view" | undefined;
    if (pageAction === "review") submittedProposalAction = "review";
    else if (pageAction === "edit") submittedProposalAction = "revise";
    // A citation of a draft is minted from persisted status, so it can name a proposal that has
    // been submitted since. Read-only reroutes the same way the editing actions do.
    else if (pageAction === "view") submittedProposalAction = "view";
    // If opening a submitted proposal through an old draft URL, reroute to the active flow.
    if (!isDeletingProposalRef.current && submittedProposalAction && proposal?.isSubmitted) {
      // The query goes along, so a link that opened the Comments pane still opens it on the active page.
      const activeProposalPath = `/proposals/active/${submittedProposalAction}/${proposal.id?.value}/details${location.search}`;
      appDirectedNavigationPathRef.current = activeProposalPath;
      navigate(activeProposalPath, { replace: true });
      return;
    }

    // Deleted (or access revoked) under the viewer while they had it open. This arrives over the
    // websocket, so nothing about the page's own load state changes — the canonical envelope still holds
    // its last good projection and the editor would happily keep going. Eject on the same terms as
    // opening a deleted proposal, before the not-found reasoning below, which cannot see this.
    if (proposalWasDeleted && !isDeletingProposalRef.current) {
      enqueueSnackbar("This proposal is no longer available.", { variant: "warning" });
      // The guard compares pathnames, and the stage chip is in the query.
      appDirectedNavigationPathRef.current = "/proposals";
      navigate(proposalListPath(ProposalStageChip.Drafts), { replace: true });
      return;
    }

    // Proposal refused or not found.
    // `proposal` is also null while a proposal is still being resolved, so the absence of a
    // proposal only means "not found" once the canonical envelope has settled. The review route says
    // what the server said too: a failed read does not mean the reader was removed as a reviewer, and
    // telling them so contradicted a list that still showed the draft awaiting their review (#6622).
    const shouldShowNotFound =
      Boolean(proposalId) &&
      !proposalId?.isEmpty &&
      proposal === null &&
      pageAction !== "create" &&
      !proposalSourceIsResolving &&
      !isDeletingProposalRef.current &&
      !isSubmittingProposalRef.current;

    if (shouldShowNotFound) {
      if (idParam) {
        enqueueSnackbar(proposalAccessWasDenied ? `${PROPOSAL_ACCESS_DENIED_MESSAGE}.` : "Proposal not found.", {
          variant: "warning",
        });
      }
      // The guard compares pathnames, and the stage chip is in the query.
      appDirectedNavigationPathRef.current = "/proposals";
      navigate(proposalListPath(ProposalStageChip.Drafts), { replace: true });
    }
  }, [
    pageAction,
    activeTab,
    location.search,
    navigate,
    proposal,
    proposalId,
    proposalSourceIsResolving,
    proposalAccessWasDenied,
    idParam,
    proposalWasDeleted,
  ]);

  // Keep refs in sync
  useEffect(() => {
    proposalRef.current = proposal;
    collaborationRef.current = collaboration;
    updatedBuilderRef.current = updatedBuilder;
    changeDescriptionContextRef.current = changeDescriptionContext;
  }, [
    proposal,
    collaboration,
    commentProposal,
    updatedBuilder,
    changeDescriptionContext,
  ]);

  // #region Populate Comment Threads
  // Loads the negotiation's comment Forums into the Forum/Message comment store the drawer and per-field
  // badges read from (and the Ably `messages` middleware updates).
  useEffect(() => {
    if (!commentProposal?.id || isLoading || loadedCommentThreads) return;
    void dispatch(populateCommentThreadsByProposal({ proposal: commentProposal, session }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [commentProposal?.id?.value, isLoading, loadedCommentThreads, session]);

  useEffect(() => {
    if (proposalId) {
      builderStorageIdRef.current = proposalId;
    } else if (!createdProposalIdAwaitingHydration) {
      builderStorageIdRef.current = Guid.EMPTY;
    }
  }, [createdProposalIdAwaitingHydration, proposalId]);


  // #region Functions
  /** Opening the pane marks nothing read (#5824): the comments the reader scrolls into view are. */
  const handleOpenCommentsPanel = useCallback(
    (field?: ProposalField, title?: string) => {
      dispatchPanelState({ type: "openComments", field, title, preserveTitleWhenUndefined: true });
    },
    [dispatchPanelState],
  );

  // A link asked for the Comments pane: `?pane=comments`, and the conversation with `&forum=` (#5826).
  // It opens once the pane is available — a draft has a conversation only once it is saved — and each
  // request opens it once. Like any other opening it marks nothing read (#5824): the comments the reader
  // then scrolls into view are.
  const { requestKey: commentsPaneRequestKey, forumRequest: commentsForumRequest } =
    useConversationPaneRequest(ConversationPane.Comments);
  const openedCommentsPaneRequestKeyRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (!commentsPaneRequestKey || !commentsPaneIsAvailable) return;
    if (openedCommentsPaneRequestKeyRef.current === commentsPaneRequestKey) return;
    openedCommentsPaneRequestKeyRef.current = commentsPaneRequestKey;
    dispatchPanelState({ type: "openComments", preserveTitleWhenUndefined: true });
  }, [commentsPaneIsAvailable, commentsPaneRequestKey, dispatchPanelState]);

  const handleOpenGeneralCommentsPanel = useCallback(() => {
    handleOpenCommentsPanel(ProposalField.General, "");
  }, [handleOpenCommentsPanel]);

  // Builder initialization
  useEffect(() => {
    if (proposalId && !canUseLegacyEditor) return;
    if (proposalId && !proposal) return; // still loading proposal
    // The save moved this editor's builder off the new-draft slot onto the created draft
    // (`promoteCreatedProposalDraft`), which empties that slot on purpose. Seeding it again here —
    // the render right after the create, while the route is still the create route — wrote the
    // cache the save had just cleared straight back to browser storage, and the next proposal
    // creation opened on it (#5667). This editor has its draft; it needs no new-draft builder.
    if (!proposalId && createdProposalIdAwaitingHydration) return;
    if (updatedBuilderJSON || updatedBuilder) return;
    if (preSelectedTeamInfo?.targetTeamId && !preSelectedMarketplaceTeam && !preSelectedMarketplaceTeamQuery.isError) {
      return;
    }
    if (seatsVendorsOwnTeam && vendorsOwnTeamsQuery.isLoading) return;

    const initializedBuilder =
      originalBuilder ??
      new ProposalBuilder({
        session,
        startingSpec: proposal?.spec,
        existingWorkAgreement,
        preSelectedClientLeader:
          session.context?.viewingAsVendor && preSelectedLeaderInfo?.hasSigningAuthority
            ? preSelectedLeaderInfo?.toEntityMember()
            : undefined,
        preSelectedVendorLeader:
          !session.context?.viewingAsVendor && preSelectedLeaderInfo?.hasSigningAuthority
            ? preSelectedLeaderInfo?.toEntityMember()
            : undefined,
        preSelectedTeam: preSelectedMarketplaceTeam?.toWorkTeam(),
        creatorMarketplaceTeamId: vendorsSoleTeam?.id,
      });
    dispatch(
      replaceOriginalProposalBuilder({
        proposalId: proposalId ?? Guid.EMPTY,
        builder: initializedBuilder,
      }),
    );
    dispatch(
      replaceUpdatedProposalBuilder({
        proposalId: proposalId ?? Guid.EMPTY,
        builder: initializedBuilder,
      }),
    );
  }, [
    createdProposalIdAwaitingHydration,
    proposal,
    proposalId,
    updatedBuilder,
    updatedBuilderJSON,
    originalBuilder,
    preSelectedLeaderInfo,
    preSelectedTeamInfo?.targetTeamId,
    preSelectedMarketplaceTeam,
    preSelectedMarketplaceTeamQuery.isError,
    seatsVendorsOwnTeam,
    vendorsOwnTeamsQuery.isLoading,
    vendorsSoleTeam,
    session,
    session.context?.viewingAsVendor,
    existingWorkAgreement,
    dispatch,
    canUseLegacyEditor,
  ]);

  // Account view switching
  /**
   * The subject this page last moved the account view for. Opening it puts the person in the party
   * they sit on, once; after that the view is theirs. Without this the effect below re-ran on every
   * new session -- including the one the account switcher publishes -- saw the "wrong" party again,
   * and switched straight back: a second PATCH undoing the first.
   */
  const accountViewAlignedFor = useRef<string | undefined | null>(null);
  useEffect(() => {
    // The server says which party this person sits on. Membership on the proposal cannot be read
    // instead: a canonical projection masks the other side's team, and `proposal` is only ever built
    // from a canonical envelope, so there is nothing to decide until that envelope arrives.
    if (collaboration.mode !== "canonical" || !collaboration.envelope) return;
    const subject = proposalId?.value;
    if (accountViewAlignedFor.current === subject) return;
    accountViewAlignedFor.current = subject;

    const accountType =
      collaboration.envelope.viewingParty === "Vendor" ? AccountType.Vendor : AccountType.Client;
    const alreadyViewingParty = session.context?.viewingAsVendor === (accountType === AccountType.Vendor);

    if (!alreadyViewingParty) void session.setAccountViewType(accountType);
  }, [collaboration.envelope, collaboration.mode, proposalId, session]);

  const activeReviewField = useMemo(() => {
    if (!updatedBuilder) return undefined;

    const proposalFieldCategories = Object.values(ProposalFieldCategory);
    const proposalFieldNames = Object.values(ProposalFieldName);
    const currentCategory = proposalFieldCategoryFromPageTab(activeTab);

    if (selectedReviewField?.category === currentCategory) return selectedReviewField;

    const tabIssues: ProposalField[] = issueFields.filter((field) => field.category === currentCategory) ?? [];

    return chain(tabIssues)
      .uniqBy((field) => `${field.category}-${field.name}-${field.id?.value ?? ""}`)
      .sortBy([
        (field) => proposalFieldCategories.indexOf(field.category),
        (field) => proposalFieldNames.indexOf(field.name),
      ])
      .head()
      .value();
  }, [activeTab, issueFields, selectedReviewField, updatedBuilder]);

  // Arriving at a stop — a new tab, or any arrival at a field — scrolls it into view once it is drawn.
  // A stop on another tab is selected a render before that tab is shown, and the tab being left would
  // scroll to its own first issue in between; the arrival waits for the stop's tab instead.
  const arrivingAtAnotherTab =
    selectedReviewField !== undefined && selectedReviewField.category !== proposalFieldCategoryFromPageTab(activeTab);
  const { traverserStopScope, attachTraverserStopScope } = useTraverserStopScope(
    arrivingAtAnotherTab ? undefined : `${activeTab}:${fieldArrivals}`,
  );

  const submitReviewTooltip = (() => {
    if (hasUserApprovedReview && hasPendingReviewerChanges) return "Save updates.";
    if (hasUserApprovedReview) return "Review complete. You can still comment.";
    if (!hasPendingReviewerChanges) return "Finish reviewing proposal.";
    return "Save updates and finish reviewing proposal.";
  })();

  function closeTabScopedPanels() {
    dispatchPanelState({ type: "closeTabScopedPanels" });
  }

  function handleTabChange(_event: ChangeEvent<object> | null, newTab: PageTab) {
    if (pageControlsArePaused) return;
    closeTabScopedPanels();

    if (!proposalId) {
      navigate(`/proposals/drafts/create/${newTab}`);
      return;
    }
    navigate(`/proposals/drafts/${actionParam}/${proposalId.value}/${newTab}`);
  }

  const handleTeamSelectionDrawerOpenChange = useCallback(
    (open: boolean) => {
      dispatchPanelState({ type: "setTeamSelectionOpen", open });
    },
    [dispatchPanelState],
  );

  function proposalFieldHasUnreadChange(field: ProposalField): boolean {
    const envelope = collaboration.mode === "canonical" ? collaboration.envelope : undefined;
    return proposalFieldUnread(envelope, field, session.user?.id?.value).unreadCount > 0;
  }

  function markTraversedFieldChangesSeen(destinationField: ProposalField) {
    const envelope = collaboration.mode === "canonical" ? collaboration.envelope : undefined;
    const field = proposalFieldToMarkSeenOnTraversal({
      sidePaneIsOpen: commentsPanelIsOpen,
      currentField: activeReviewField,
      destinationField,
    });
    // A fee-schedule category acknowledges itself and not its siblings (#5118).
    const path = field && (proposalTimelineItemPathForField(field) ?? proposalTimelinePathForField(field));
    if (!envelope || !path) return;
    const { unreadCount, latestUnseenSequence } = proposalFieldUnread(envelope, field, session.user?.id?.value);
    if (unreadCount > 0) {
      void markProposalChangesSeen({
        negotiationId: envelope.negotiationId,
        path,
        throughStreamVersion: latestUnseenSequence,
      });
    }
  }

  // The two questions a landing asks of the envelope, as this render answers them. The handler below
  // is memoized on the tab, and a change that reaches the page while the reader stays on one tab
  // would otherwise be read from the envelope the page held when the tab last changed: the landing
  // found nothing unread, acknowledged nothing, and the count never dropped (#6617).
  const traversalAcknowledgementRef = useRef({ proposalFieldHasUnreadChange, markTraversedFieldChangesSeen });
  useEffect(() => {
    traversalAcknowledgementRef.current = { proposalFieldHasUnreadChange, markTraversedFieldChangesSeen };
  });

  const handleTraverseToNewField = useCallback(
    (field?: ProposalField, source: ProposalFieldTraversalSource = ProposalFieldTraversalSource.Field) => {
      if (pageControlsArePaused) return;
      if (!field) {
        dispatchPanelState({ type: "setSelectedReviewField", field, closePanels: true });
        return;
      }

      // A stop on an unread change is a stop about that change, so it opens that field's own change
      // history rather than the proposal's one conversation, which is what the pane shows and which is
      // not what the reader was sent here to read. Asked BEFORE acknowledging, since acknowledging is
      // what makes the change no longer unread.
      //
      // Only "Prev / Next Change" opens it. This handler is also every field's own traverse-in —
      // focusing a field, or clicking its Edit button, reports itself here — and a reader who reached
      // for Edit asked to type, not to read history.
      const stoppedOnUnreadChange =
        source === ProposalFieldTraversalSource.Traverser &&
        traversalAcknowledgementRef.current.proposalFieldHasUnreadChange(field);
      // Arriving at a field never opens the pane (#6625); a focus or an Edit press keeps an open one
      // open on that field. An issue stop answers itself where it lands: setting the review field below
      // scrolls the highlighted row into view.
      const keepCommentsPaneOpen =
        commentsPaneIsAvailable &&
        proposalTraversalKeepsCommentsPaneOpen({ source, field, commentsPaneIsOpen: commentsPanelIsOpen });
      // Stopping on a field is also reading it: surface what the stop is about and acknowledge what it
      // surfaced, so pressing Next repeatedly walks the unread changes down to none and the traverser
      // disables itself. A draft with no pane to surface just moves to the field; asking for a pane
      // that cannot render only opens a gap where it would have been.
      traversalAcknowledgementRef.current.markTraversedFieldChangesSeen(field);
      dispatchPanelState({
        type: "setSelectedReviewField",
        field,
        closePanels: true,
        openComments: keepCommentsPaneOpen,
        traversed: source === ProposalFieldTraversalSource.Traverser,
      });
      if (stoppedOnUnreadChange) {
        setFieldHistoryToOpen(field);
      } else {
        // Moving on answers any standing request: leaving the previous field's history asked-for would
        // hold that panel open over a field the reader has already left.
        setFieldHistoryToOpen(undefined);
      }
      // Surfacing the pane marks nothing read (#5824): a comment is read when it is scrolled into view,
      // on the tab it belongs to.

      const targetTab = pageTabFromProposalFieldCategory(field.category);
      const currentTabCategory = proposalFieldCategoryFromPageTab(activeTab);

      if (field.category !== currentTabCategory) {
        const action = actionParam ?? "create";

        if (proposalId?.value) {
          navigate(`/proposals/drafts/${action}/${proposalId.value}/${targetTab}`, {
            replace: true,
          });
        } else {
          navigate(`/proposals/drafts/create/${targetTab}`, {
            replace: true,
          });
        }
      }
    },
    [
      activeTab,
      actionParam,
      commentsPaneIsAvailable,
      commentsPanelIsOpen,
      dispatchPanelState,
      navigate,
      pageControlsArePaused,
      proposalId,
    ],
  );

  /** A deliberate "Prev / Next Change" press — the only traversal that opens the stop's own history. */
  const handleTraverseFromTraverser = useCallback(
    (field?: ProposalField) => handleTraverseToNewField(field, ProposalFieldTraversalSource.Traverser),
    [handleTraverseToNewField],
  );

  /**
   * The "Prev / Next" stops that are not fields: one for each conversation with unread comments, after
   * every field and before the walk wraps to the first (#5824). It opens the pane on that conversation's
   * tab, scrolled to its oldest unread comment — where the reader's unread reading begins. Arriving
   * marks nothing read; the comments the reader then sees are.
   */
  const handleTraverseToUnreadComments = useCallback(
    (isExternal: boolean) => {
      if (pageControlsArePaused) return;
      dispatchPanelState({
        type: "arriveAtUnreadComments",
        isExternal,
        commentToScrollTo: isExternal ? oldestUnreadAllStakeholdersComment : oldestUnreadOwnTeamComment,
      });
      setFieldHistoryToOpen(undefined);
    },
    [dispatchPanelState, oldestUnreadAllStakeholdersComment, oldestUnreadOwnTeamComment, pageControlsArePaused],
  );


  // The anchor is passed in rather than read off the event, because the control the selector hangs
  // from is not always this action's own button: once the secondary actions collapse it is the "More
  // Actions" button, and the menu item that was pressed is gone by the time the popover opens.
  function handleManageReviewersClicked(anchor: HTMLButtonElement) {
    dispatchPanelState({ type: "openReviewerSelector", anchor });
  }

  async function handleSubmitClicked() {
    if (!updatedBuilder) {
      throw new Error("Proposal cannot be submitted without a builder.");
    }

    // Reviewer changes are resolved in-field as targets, so an outstanding reviewer approval does not
    // gate submission: the dialog warns that a review is still pending (#5128) and submits anyway.
    //
    // The submission asks exactly one question, and it asks it against the SAVED state: the pending
    // edits are flushed first so the dialog can name the real change list (and let the leader withhold
    // individual changes) instead of confirming blind and asking a second time afterwards. A flush is
    // not a commitment — the work is saved either way the moment they press Save; only submitting
    // sends it to the other party.
    //
    // A draft nobody has saved is the one case where that order is wrong, because the flush CREATES the
    // proposal: the click wrote a draft and moved the route to it, and only then asked whether to
    // submit, so a Cancel left behind a draft the user never agreed to make. It has nothing published
    // to diff against either, so the dialog it would reach after the save shows the plain confirmation
    // and no change list — the same question, and it can be asked from the editor alone. Ask it first,
    // then save, submit and redirect on the one Yes (#5666). Asked ONCE: the post-save ask below is
    // skipped for this draft.
    const submittingUnsavedDraft = !proposalId && !createdProposalRef.current?.id;
    if (submittingUnsavedDraft) {
      const unsavedDraftSubmission = await resolveUnsavedDraftSubmission({
        title: "Submit Proposal",
        confirmButtonText: "Submit",
        viewingParty: isViewingAsVendor ? "Vendor" : "Client",
        // Nothing is persisted, so no reviewer on this draft holds an approval of it.
        reviewerCount: reviewers.length,
        recipient: submitRecipient,
      });
      if (unsavedDraftSubmission === CANCELED_SUBMISSION) return;
    }

    isSubmittingProposalRef.current = true;
    // Submission begins with an internal save so the dialog can describe the acknowledged changes.
    // Surface that whole operation as Submit work; otherwise the button remains idle during the
    // slowest part of the click and the page looks hung.
    setIsSubmitting(true);
    const submittingNewDraft = !proposalId;
    const createdDraftTab = activeTab ?? PageTab.Details;

    try {
      const submissionMode = collaborationRef.current.mode;
      // A save that answers null has already said why; a second error beside it reads as two
      // failures (#6628, #6614). Any other save with no id has said nothing yet.
      const draftProposalIdValue = await handleSaveChanges({
        confirmSave: false,
      });
      if (draftProposalIdValue === null) return;
      if (!draftProposalIdValue) {
        throw new Error("Failed to save proposal before submission.");
      }
      // Saving a new draft creates it, and `promoteCreatedProposalDraft` moves its builder from the
      // new-draft slot to the created id. The create route still reads the new-draft slot, which that
      // move empties, so the page would be left on an editor with nothing in it — which is where a
      // submission that then fails puts the user. Move to the saved draft's edit route as soon as the
      // save resolves, as Save does (#5640).
      if (submittingNewDraft) {
        const createdDraftPath = `/proposals/drafts/edit/${draftProposalIdValue}/${createdDraftTab}`;
        appDirectedNavigationPathRef.current = createdDraftPath;
        navigate(createdDraftPath, { replace: true });
      }

      let submittedProposalIdValue: string | undefined;
      const viewerScope = collaborationRef.current.viewerScope;
      if (!viewerScope) throw new Error("Proposal collaboration viewer is not ready for submission.");

      if (submissionMode === "canonical") {
        // `savePendingChanges` reads Redux at invocation time. Calling it after
        // the page save gives submission the acknowledged envelope cursors,
        // never the envelope captured by the render that started this click.
        let submissionEnvelope = await collaborationRef.current.savePendingChanges();
        if (!submissionEnvelope) {
          throw new Error("Canonical proposal save returned no envelope before submission.");
        }
        const submission = await resolveSubmission({
          envelope: submissionEnvelope,
          title: "Submit Proposal",
          confirmButtonText: "Submit",
          context: changeDescriptionContext,
          recipient: submitRecipient,
          viewerScope,
        });
        if (submission === CANCELED_SUBMISSION) return;
        if (submission.revertSelection.length > 0) {
          const revertOperations = buildProposalSubmissionRevertOperations({
            envelope: submissionEnvelope,
            selections: submission.revertSelection,
          });
          if (revertOperations.length !== submission.revertSelection.length) {
            throw new Error("One or more proposal changes could not be reverted before submission.");
          }
          dispatch(
            proposalOperationsQueued({
              negotiationId: submissionEnvelope.negotiationId,
              operations: revertOperations,
            }),
          );
          const revertedEnvelope = await collaborationRef.current.savePendingChanges();
          if (!revertedEnvelope) {
            throw new Error("Reverted proposal changes could not be saved before submission.");
          }
          submissionEnvelope = revertedEnvelope;
        }
        const submittedEnvelope = await submitProposalCollaborationDraft({
          negotiationId: submissionEnvelope.negotiationId,
          viewerScope,
          command: buildSubmitProposalDraftCommand({
            envelope: submissionEnvelope,
          }),
        }).unwrap();
        dispatch(proposalEnvelopeReceived({ envelope: submittedEnvelope, viewerScope }));
        submittedProposalIdValue = submittedEnvelope.projection.id ?? undefined;
      } else if (submissionMode === "disabled") {
        // A brand-new proposal has no id (and therefore no negotiation hook)
        // until its initial create completes. Resolve that newly-created
        // stream, then submit through the same typed command as every other
        // canonical draft.
        const submissionEnvelope = await loadProposalCollaboration(
          { proposalId: draftProposalIdValue, viewerScope },
          false,
        ).unwrap();
        // No question here: this branch is reached only by a draft that was unsaved when Submit was
        // pressed, and that draft answered before the save above. `disabled` is the mode of an editor
        // with no route id, and a save from that editor after a create has already resolved refuses to
        // write (the non-canonical guard in `persistProposalChanges`), so a draft that already had an
        // id never gets this far. Asking again would be the second dialog #5666 removed.
        const submittedEnvelope = await submitProposalCollaborationDraft({
          negotiationId: submissionEnvelope.negotiationId,
          viewerScope,
          command: buildSubmitProposalDraftCommand({
            envelope: submissionEnvelope,
          }),
        }).unwrap();
        dispatch(proposalEnvelopeReceived({ envelope: submittedEnvelope, viewerScope }));
        submittedProposalIdValue = submittedEnvelope.projection.id ?? undefined;
      } else {
        throw new Error("Proposal collaboration is not ready for submission.");
      }

      if (!submittedProposalIdValue) {
        throw new Error("Submitted proposal did not return an id.");
      }

      const submittedProposalId = new Guid(submittedProposalIdValue);
      dispatch(clearLocalProposalById(submittedProposalId));
      dispatch(clearProposalBuilderByProposalId(submittedProposalId));
      if (proposalId && !proposalId.isEqualTo(submittedProposalId)) {
        dispatch(clearLocalProposalById(proposalId));
        dispatch(clearProposalBuilderByProposalId(proposalId));
      }
      if (!proposalId) dispatch(clearNewProposalBuilder());

      enqueueSnackbar("Proposal submitted successfully.", {
        variant: "success",
      });

      navigate(`/proposals/active/revise/${submittedProposalIdValue}/details`, { replace: true });
    } catch (error) {
      console.error(error);
      // A `409` is a genuine content conflict, not a transport failure: the
      // other party changed a field this draft also touched. Name the fields
      // instead of reporting a bare failure the user cannot act on.
      const conflictSummary = describeProposalConflicts(
        readProposalConflictResponse(error)?.conflicts ?? [],
      );
      // A `400` means the command itself was refused — most often a draft that fails validation, which
      // is persisted and so fails identically after a refresh. Reporting a bare failure there gave the
      // user a dead end: nothing to fix, and no reason to believe retrying would ever work.
      if (conflictSummary) {
        showActionableErrorSnackbar(`${conflictSummary} ${PROPOSAL_CHANGED_MESSAGE}`, { autoHideDuration: 12000 });
      } else {
        showProposalCommandError(error, "submitting your proposal", "proposal.submit", { autoHideDuration: 12000 });
      }
    } finally {
      isSubmittingProposalRef.current = false;
      setIsSaving(false);
      setIsSubmitting(false);
    }
  }

  function shouldDisableTabs(): boolean {
    return pageControlsArePaused || isManagingReviewers || isSelectingClient;
  }

  /**
   * Hands the draft over to its primary negotiator, who owns it from then on and alone may submit it
   * (#6579). One-way: the creator keeps their reviewer seat, and the new owner can name another
   * team leader later.
   */
  async function handleHandOverClicked() {
    const proposalId = proposal?.id?.value;
    const viewerScope = collaborationRef.current.viewerScope;
    if (!proposalId || !viewerScope) return;

    const response = await confirm({
      title: `Hand over to ${primaryNegotiatorName}?`,
      message:
        `${primaryNegotiatorName} becomes the owner of this draft, and the only person who can submit it.\n\n` +
        `\u2022 ${primaryNegotiatorName} is told the draft is ready for them.\n` +
        "\u2022 You stay on the draft as a reviewer.\n" +
        `\u2022 You cannot take it back. ${primaryNegotiatorName} can name a different team leader later.`,
      okButtonText: "Hand Over",
    });
    if (response === ConfirmResponse.Cancel) return;

    const handedOverMessage = `Handed over to ${primaryNegotiatorName}.`;
    try {
      await handOverProposalDraft({ negotiationId: proposalId, viewerScope }).unwrap();
      enqueueSnackbar(handedOverMessage, { variant: "success" });
    } catch (error) {
      console.error(error);
      showProposalCommandError(error, "handing the draft over", "proposal.hand-over");
    }
  }

  async function handleDeleteClicked() {
    if (proposal?.isArchived) return;
    if (!proposal?.id) {
      console.warn("Proposal not found.");
      return;
    }

    try {
      const rejectResponse = await confirm({
        title: "Delete Draft Proposal?",
        message: "This cannot be undone.",
      });

      if (rejectResponse === ConfirmResponse.Cancel) return;

      setIsDeleting(true);
      isDeletingProposalRef.current = true;
      await deleteProposalQuery({ proposalId: proposal.id.value }).unwrap();
      handleProposalDraftCleanup(proposal.id);
      enqueueSnackbar("Proposal deleted.", { variant: "info" });
      navigate(proposalListPath(ProposalStageChip.Drafts), { replace: true });
    } catch (error) {
      // Only a failed delete clears the flag. Cleared on success, it would subscribe the page to the
      // deleted draft's envelope again for the render before the navigation unmounts it (#6671).
      isDeletingProposalRef.current = false;
      setIsDeleting(false);
      console.error(error);
      showDeleteFailure(error);
    }
  }

  /**
   * Sends identical copies of this draft to several firms (#576). The draft is what every copy is
   * made from; nothing the client edits here is sent until they press Send in the dialog.
   */
  async function handleSendToFirms(firms: readonly ProposalFirm[]) {
    const proposalToSend = updatedBuilder?.buildNewDraft();
    if (!proposalToSend) throw new Error("There is no proposal to send.");
    const plan = planProposalFirmSend(
      firmSendAttemptRef.current,
      proposalFirmSendTerms(proposalToSend, session),
      () => crypto.randomUUID(),
    );
    const attempt = plan.attempt;
    firmSendAttemptRef.current = attempt;
    await discardEarlierFirmSend(plan.staleCopyIds, plan.earlierOutcomeUnknown);
    return sendProposalToFirms({ proposal: proposalToSend, firms, session, commandId: attempt.commandId })
      .unwrap()
      .then(
        (results) => {
          firmSendAttemptRef.current = recordProposalFirmSendResults(attempt, results);
          return results;
        },
        (error: unknown) => {
          firmSendAttemptRef.current = recordProposalFirmSendFailure(attempt, error);
          throw error;
        },
      );
  }

  /**
   * The draft changed since an earlier send that no firm received. That send's copies carry the old
   * terms, so they are deleted rather than sent, and the new send drafts every firm afresh.
   */
  async function discardEarlierFirmSend(staleCopyIds: readonly string[], earlierOutcomeUnknown: boolean) {
    if (earlierOutcomeUnknown) {
      enqueueSnackbar(
        "An earlier send to several firms did not finish, so some firms may have its terms. " +
          "Check your active proposals and drafts.",
        { variant: "warning", autoHideDuration: 12000 },
      );
    }
    const deletions = await Promise.allSettled(
      staleCopyIds.map((proposalId) => deleteProposalQuery({ proposalId }).unwrap()),
    );
    if (deletions.some((deletion) => deletion.status === "rejected")) {
      enqueueSnackbar("Some unsent copies from the earlier send could not be deleted. Delete them from your drafts.", {
        variant: "warning",
      });
    }
  }

  /**
   * Once at least one firm has its own copy, the draft they were made from has done its work: it is
   * deleted so it cannot be sent a second time by mistake, and the client goes on to compare the
   * proposals the send produced (#6457).
   */
  async function handleFirmSendClosed(sentRequestId: string | undefined) {
    setIsFirmSendOpen(false);
    if (!sentRequestId) return;

    firmSendAttemptRef.current = undefined;
    const draftId = proposal?.id ?? createdProposalRef.current?.id;
    // Leave the page before the draft is deleted. Deleting it first had this page, still open on it,
    // read the deleted draft's negotiation back and answer 404.
    isDeletingProposalRef.current = Boolean(draftId);
    handleProposalDraftCleanup(draftId);
    navigate(proposalComparisonPath(sentRequestId), { replace: true });
    if (!draftId) return;
    try {
      await deleteProposalQuery({ proposalId: draftId.value }).unwrap();
    } catch {
      enqueueSnackbar("The proposal was sent, but its draft could not be deleted. Delete it from your drafts.", {
        variant: "warning",
      });
    }
  }

  async function handleProposalBuilderUpdated(updatedProposalBuilder: ProposalBuilder | undefined) {
    if (reviewCompletionLocksEditing) {
      return;
    }
    if (!updatedProposalBuilder) {
      console.warn("handleProposalBuilderUpdated called with empty proposal builder");
      return;
    }
    if (collaboration.mode === "canonical") {
      if (!effectiveProposal) throw new Error("Canonical proposal projection is unavailable.");
      updatedBuilderRef.current = updatedProposalBuilder;
      const updatedProposal = effectiveProposal.isSubmitted
        ? updatedProposalBuilder.updateSubmittedDraftReviewProposal(effectiveProposal)
        : updatedProposalBuilder.updateDraftProposal(effectiveProposal);
      collaboration.queueProposalUpdate(updatedProposal);
      return;
    }
    const builderToStore = getDraftBuilderForUpdate({
      originalBuilder,
      proposal,
      updatedBuilder: updatedProposalBuilder,
    });
    // Keep imperative actions (save, reviewer send, and cross-tab edits) on the
    // exact builder that was just produced. A debounced input flushes on blur,
    // before the following click; deferring this Redux update as a transition
    // allowed that click to retain and later save the previous render's builder.
    updatedBuilderRef.current = builderToStore;
    dispatch(
      replaceUpdatedProposalBuilder({
        proposalId: builderStorageIdRef.current,
        builder: builderToStore,
      }),
    );
  }

  const handleProposalDraftCleanup = useCallback(
    (proposalId: Guid | undefined) => {
      if (proposalId && !proposalId.isEmpty) {
        dispatch(clearUnsavedCommentsByProposalId(proposalId));
        if (collaboration.mode === "canonical") {
          collaboration.discardPendingChanges();
        } else {
          dispatch(clearProposalBuilderByProposalId(proposalId));
        }
      } else {
        dispatch(clearNewProposalBuilder());
      }
    },
    [collaboration, dispatch],
  );

  const handleCloseCommentsPanel = () => {
    dispatchPanelState({ type: "closeComments" });
  };

  async function completeCurrentReview() {
    const currentCollaboration = collaborationRef.current;
    if (currentCollaboration.mode === "canonical") {
      // Always read through the hook after Save; it returns the latest Redux
      // envelope even when there is no remaining command to send.
      const freshEnvelope = await currentCollaboration.savePendingChanges();
      if (!freshEnvelope || !currentCollaboration.viewerScope) {
        throw new Error("Canonical proposal review is missing its authoritative envelope.");
      }
      await completeProposalReview({
        negotiationId: freshEnvelope.negotiationId,
        viewerScope: currentCollaboration.viewerScope,
        command: buildCompleteProposalReviewCommand({ envelope: freshEnvelope }),
      }).unwrap();
      return;
    }

    throw new Error("Proposal collaboration is not ready to complete a review.");
  }

  async function handleFinalizeReviewClicked() {
    if (!proposal?.id) throw new Error("No proposal to finalize review for.");

    try {
      // The review button routes pending local work through handleSubmitAllAndFinalizeReviewClicked, so
      // a plain "Complete Review" is a versioned endorsement of the freshest canonical envelope and
      // nothing else — `completeCurrentReview` reads that envelope through `savePendingChanges`, which
      // sends a command only when something is actually queued (ARCHITECTURE.md, "Complete review":
      // completion "runs after pending edits are saved", and a reviewer with no pending edits has none
      // to save). This used to pre-save unconditionally. A comment-only reviewer (#4535) has neither
      // `canEditDraft` nor `canManageReviewers`, so that save tripped its own permission gate, returned
      // no id, and turned a legitimate completion into "You do not have permission to save updates to
      // this proposal." followed by "Failed to finalize proposal review." — with no request ever sent.
      await completeCurrentReview();
      handleProposalDraftCleanup(proposal.id);
      enqueueSnackbar("Proposal review finalized.", { variant: "success" });
      navigate(proposalListPath(ProposalStageChip.Drafts), { replace: true });
    } catch (error) {
      console.error(error);
      showProposalCommandError(error, "finalizing your review", "proposal.finalize-review");
    }
  }

  async function handleSubmitAllAndFinalizeReviewClicked() {
    if (!proposalRef.current?.id) throw new Error("No proposal to finalize review for.");

    try {
      // Only the reviewer's proposal-content work. "Save & Complete Review" used to also flush the
      // reviewer's comments, because a review whose comments stayed in the browser was delivered to
      // nobody; a comment reaches its audience when it is written now, so completing a review has
      // nothing of the conversation left to send.
      // `handleSaveChanges` refuses to persist for an actor who may not, so asking it to save
      // unsendable work would only fail the completion. A comment-only reviewer can still be holding
      // queued content — a durable local queue built while they had edit rights that were later
      // revoked — and that work is unsendable by definition: the server rejects a save carrying any
      // operation from an actor without `canEditDraft` (#4535). Skip the save and endorse the current
      // version; the local work stays queued rather than being destroyed.
      if (hasPendingReviewerChanges && collaboration.canPersistProposalUpdates(proposalRef.current)) {
        const savedProposalId = await handleSaveChanges({
          confirmSave: false,
        });

        if (!savedProposalId) {
          throw new Error("Failed to save proposal updates before finalizing review.");
        }
      }

      await completeCurrentReview();
      handleProposalDraftCleanup(proposalRef.current.id);
      enqueueSnackbar("Proposal review finalized.", { variant: "success" });
      navigate(proposalListPath(ProposalStageChip.Drafts), { replace: true });
    } catch (error) {
      console.error(error);
      showProposalCommandError(error, "finalizing your review", "proposal.finalize-review");
    }
  }

  const persistProposalChanges = useCallback(
    async (args?: {
      proposal?: Proposal;
      proposalBuilder?: ProposalBuilder;
      confirmSave?: boolean;
      confirmationMode?: ConfirmationMode;
    }): Promise<string | null | undefined> => {
      const { proposal, proposalBuilder, confirmSave = true, confirmationMode = "review" } = args ?? {};

      // This handler keeps a stable identity and reads mutable state through refs. The
      // collaboration must come through its ref too: read from the closure it would be
      // whatever mode was current on first render — still "loading" — which sent every
      // save down the legacy path while edits queued canonically and were left behind.
      const collaboration = collaborationRef.current;

      // A proposal whose source failed to answer is a paused page, not an empty one. `proposal` is
      // null while the route still names a draft, so a save let through here would write a SECOND
      // draft beside the one that could not be loaded. Loading already pauses the page; a load error
      // has to as well.
      if (collaboration.mode === "error") {
        showActionableErrorSnackbar(UNLOADED_PROPOSAL_SAVE_MESSAGE);
        return null;
      }

      // A persisted route stays on the canonical command path while its envelope loads. A just-created
      // draft is persisted too, even before its route and envelope hydrate. Treating either window as a
      // legacy draft would write through Proposal PATCH, bypassing the event stream and leaving queued
      // edits unsaved. Keep the editor values local until the envelope arrives so the user can save them.
      if (
        collaboration.mode !== "canonical" &&
        (proposalId || proposalRef.current?.id || createdProposalRef.current?.id)
      ) {
        showActionableErrorSnackbar(
          collaboration.mode === "loading"
            ? "This proposal's negotiation is still loading. Wait for it to load, then save your changes again."
            : UNLOADED_PROPOSAL_SAVE_MESSAGE,
        );
        return null;
      }

      let proposalToSave = proposal ?? proposalRef.current ?? createdProposalRef.current;
      const builderToSave = proposalBuilder ?? updatedBuilderRef.current;

      const proposalCanSave = () => {
        const currentBuilder = proposalBuilder ?? updatedBuilderRef.current ?? builderToSave;
        return currentBuilder
          ? ProposalIssues.fromBuilder(currentBuilder).canSave
          : updatedBuilderIssuesRef.current.canSave;
      };

      if (!proposalCanSave()) {
        showActionableErrorSnackbar("Resolve the required proposal issues before saving.");
        return null;
      }

      setIsSaving(true);

      try {
        if (!proposalToSave) {
          proposalToSave = builderToSave?.buildNewDraft();
        } else if (builderToSave) {
          proposalToSave = proposalToSave.isSubmitted
            ? builderToSave.updateSubmittedDraftReviewProposal(proposalToSave)
            : builderToSave.updateDraftProposal(proposalToSave);
        }

        if (!proposalToSave) {
          const missing = new Error("No proposal to save");
          console.error(missing);
          showErrorSnackbar(missing, { doing: "saving your proposal changes", operation: "proposal.save" });
          return;
        }

        const updatedReviewers = session.user?.isClient(proposalToSave)
          ? proposalToSave.clientReviewers
          : proposalToSave?.vendorReviewers;
        const baselineProposal = proposal ?? proposalRef.current ?? createdProposalRef.current;
        const baselineProposalToSave = baselineProposal ?? proposalToSave;
        const originalReviewers =
          (session.user?.isClient(proposalToSave)
            ? baselineProposalToSave.clientReviewers
            : baselineProposalToSave.vendorReviewers) ?? [];
        const builderWithSavedReviewers =
          baselineProposal && builderToSave
            ? builderToSave
                .setClientReviewers(baselineProposal.clientReviewers)
                .setVendorReviewers(baselineProposal.vendorReviewers)
            : undefined;
        const hasNonReviewerBuilderUpdates = Boolean(baselineProposal?.isModifiedByBuilder(builderWithSavedReviewers));
        const reviewerActionState = getReviewerActionState({
          originalReviewers,
          selectedReviewers: updatedReviewers,
          proposalIsModifiedByBuilder: hasNonReviewerBuilderUpdates,
          proposalHasUnsavedRedlineChanges: false,
        });
        const hasReviewerContext = updatedReviewers.length > 0 || reviewerActionState.hasReviewerChanges;

        if (confirmSave) {
          // Describe exactly what the command about to be sent carries — the queued operations and
          // reviewer changes — in the same words the field change history uses, and name who receives
          // it. A builder-derived log described a different set in a different vocabulary.
          const sharePlan = buildProposalSaveSharePlan({
            operations: collaboration.pendingChanges.operations,
            reviewerChanges: collaboration.pendingChanges.reviewerChanges,
            audienceLabel: proposalSaveAudienceLabel({
              reviewerCount: updatedReviewers.length,
              isReviewing,
            }),
            // Through the ref: this callback's closure predates the individuals cache and the team.
            context: changeDescriptionContextRef.current,
          });
          const confirmationCopy = getSaveConfirmationCopy({
            confirmationMode,
            hasPendingChanges: hasNonReviewerBuilderUpdates,
            hasReviewerChanges: reviewerActionState.hasReviewerChanges,
            hasReviewerContext,
            hasReviewerRecipients: updatedReviewers.length > 0,
            proposalIsSaved: Boolean(proposalToSave.id),
            reviewerConfirmMessage: reviewerActionState.confirmMessage,
            reviewerConfirmOkButtonText: reviewerActionState.confirmOkButtonText,
            reviewerConfirmSupplementalMessage: reviewerActionState.confirmSupplementalMessage,
            reviewerConfirmTitle: reviewerActionState.confirmTitle,
            audienceLabel: sharePlan.audienceLabel,
            // The dialog renders this list below the message, so the message stops summarising it.
            describedChangeCount: sharePlan.contentChangeCount,
            describedReviewerChangeCount: sharePlan.reviewerChangeCount,
          });

          const response = await confirm({
            title: confirmationCopy.title,
            // The change list renders rather than flattening into the message string, so it keeps its
            // bullets and shows each value transition as a redline instead of plain grey text. Suppressed
            // entirely on a draft's first-ever save (#4952): there is no prior version to diff against,
            // so the list would only repeat what the actor just typed.
            message: (
              <>
                {confirmationCopy.message}
                {confirmationCopy.showsChangeSummary && (
                  <ProposalChangeSummaryList
                    heading={confirmationCopy.changeLogHeading}
                    descriptors={sharePlan.descriptors}
                  />
                )}
              </>
            ),
            okButtonText: confirmationCopy.okButtonText,
            // Deliberately no comment option. A save used to offer "Save and publish pending internal
            // comments", because a comment sat in the browser until one did; comments are written as
            // they are made, so a save has nothing of the conversation to offer.
          });

          if (response === ConfirmResponse.Cancel) return null;

          // The user can keep editing while this dialog is open. Recheck the current builder before
          // any request so the confirmation cannot authorize values that are now invalid.
          if (!proposalCanSave()) {
            showActionableErrorSnackbar("Resolve the required proposal issues before saving.");
            return null;
          }
        }

        const canPersistProposalUpdates = collaboration.canPersistProposalUpdates(proposalToSave);

        // A comment-only reviewer used to be let through here so their staged comments could be
        // published by the save; they have none to publish, because their comments went out as they
        // wrote them. Save is once again exactly about proposal-content and reviewer changes.
        // A staged client team roster is written by this same press and is gated by its own
        // capability, so it is not something this actor may be turned away from (#5116).
        if (baselineProposal && !canPersistProposalUpdates && !collaboration.hasPendingClientTeamMembers) {
          enqueueSnackbar("You do not have permission to save updates to this proposal.", {
            variant: "warning",
          });
          return;
        }

        if (collaboration.mode === "canonical") {
          let savedEnvelope = collaboration.envelope;
          let hasCanonicalUpdates = false;
          // Only an actor permitted to persist proposal-content/reviewer changes queues and flushes
          // them. A comment-only reviewer skips this entirely and falls through to publishing comments.
          if (canPersistProposalUpdates) {
            // This re-flatten only catches a final un-queued edit; it must never retract queued local
            // work. `proposalToSave` is rebuilt from refs and can lag the Redux queue after an async
            // collection add (a conflicts document or fee category just added), so without this guard a
            // stale snapshot would emit a spurious CollectionItemRemoved that composes the genuine
            // pending add to null and the save would find nothing to persist.
            const queuedUpdate = collaboration.queueProposalUpdate(proposalToSave, {
              preservePendingCollectionAdds: true,
            });
            hasCanonicalUpdates = collaboration.hasPendingChanges || queuedUpdate;
          }
          // Outside the content gate: the roster is reference state with a capability of its own,
          // so it is saved by this press whether or not this actor may edit the draft (#5116).
          hasCanonicalUpdates = hasCanonicalUpdates || collaboration.hasPendingClientTeamMembers;
          if (hasCanonicalUpdates) {
            savedEnvelope = await collaboration.savePendingChanges();
            if (!savedEnvelope) throw new Error("Canonical proposal save returned no envelope.");
          }

          if (hasCanonicalUpdates) {
            enqueueSnackbar(
              getProposalSaveSuccessMessage({
                confirmationMode,
                hasReviewerOnlyUpdates: hasReviewerContext && !reviewerActionState.hasPendingSaveUpdates,
              }),
              { variant: "success" },
            );

            // S2 (ROADMAP.md): a save is never rejected for a content conflict, so the 200 above can
            // still have left part of what was just saved un-persisted. Surface it — a silent partial
            // success must never look identical to a full one.
            const dropWarning = getProposalOperationDropWarning(savedEnvelope?.operationReceipts);
            if (dropWarning) enqueueSnackbar(dropWarning, { variant: "warning", persist: true });
          }

          return savedEnvelope?.projection.id ?? proposalToSave.id?.value;
        }

        let savedProposalJSON: ProposalJSON | null = null;
        // Create only while this editor has no proposal at all. `proposalToSave.id` alone was the old
        // test, and it is null again on the very next render after a create.
        const persistedProposalId = proposalToSave.id ?? createdProposalRef.current?.id;
        if (!persistedProposalId) {
          savedProposalJSON = await createProposalQuery({
            proposal: proposalToSave,
            session,
            commandId: draftCreationCommandIdRef.current,
          }).unwrap();
          if (savedProposalJSON && session.user) {
            const savedProposal = Proposal.fromJSON({
              json: savedProposalJSON,
              currentUser: session.user,
            });
            if (savedProposal?.id) {
              const persistedBuilder = new ProposalBuilder({
                session,
                startingSpec: savedProposal.spec,
              });
              builderStorageIdRef.current = savedProposal.id;
              proposalRef.current = savedProposal;
              createdProposalRef.current = savedProposal;
              updatedBuilderRef.current = persistedBuilder;
              setCreatedProposalIdAwaitingHydration(savedProposal.id.value);
              dispatch(
                promoteCreatedProposalDraft({
                  proposal: savedProposalJSON,
                  builder: persistedBuilder,
                }),
              );
              proposalToSave = savedProposal;
            }
          }
        } else if (proposalIsModifiedAgainstBaseline(proposalToSave, baselineProposalToSave)) {
          savedProposalJSON = await saveProposalQuery({
            originalProposal: baselineProposalToSave,
            updatedProposal: proposalToSave,
            session,
          }).unwrap();
          if (!savedProposalJSON) {
            const unanswered = new Error("The save answered without a proposal");
            console.error(unanswered, saveProposalResult.error);
            showErrorSnackbar(unanswered, {
              doing: getProposalSaveFailureDoing({
                confirmationMode,
                hasReviewerOnlyUpdates: hasReviewerContext && !reviewerActionState.hasPendingSaveUpdates,
              }),
              operation: "proposal.save",
            });
            return null;
          }
          if (savedProposalJSON && session.user) {
            const savedProposal = Proposal.fromJSON({
              json: savedProposalJSON,
              currentUser: session.user,
              options: { withRedline: true },
            });
            if (savedProposal) {
              proposalRef.current = savedProposal;
            }
          }

          const successMessage = getProposalSaveSuccessMessage({
            confirmationMode,
            hasReviewerOnlyUpdates: hasReviewerContext && !reviewerActionState.hasPendingSaveUpdates,
          });
          enqueueSnackbar(successMessage, {
            variant: "success",
          });
        }

        return savedProposalJSON?.id ?? proposalToSave.id?.value;
      } finally {
        setIsSaving(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [saveProposalResult.error],
  );

  /**
   * Saves once, however many times it is asked to.
   *
   * The check-and-set is synchronous — before any state update or await — so a second entry cannot
   * slip in behind React's commit and take the create branch again. It joins the save already running
   * instead, which is what a second "Save & Close" wants anyway: the id the first one is minting.
   */
  const handleSaveChanges = useCallback(
    (args?: {
      proposal?: Proposal;
      proposalBuilder?: ProposalBuilder;
      confirmSave?: boolean;
      confirmationMode?: ConfirmationMode;
    }): Promise<string | null | undefined> => {
      const saveAlreadyRunning = saveInFlightRef.current;
      if (saveAlreadyRunning) return saveAlreadyRunning;

      const save = persistProposalChanges(args).finally(() => {
        saveInFlightRef.current = null;
      });
      saveInFlightRef.current = save;
      return save;
    },
    [persistProposalChanges],
  );

  const handleSaveUpdatesClicked = useCallback(
    async (args?: {
      confirmSave?: boolean;
      confirmationMode?: ConfirmationMode;
    }): Promise<string | null | undefined> => {
      try {
        const savedProposalId = await handleSaveChanges({
          confirmSave: args?.confirmSave ?? true,
          confirmationMode: args?.confirmationMode,
        });

        if (savedProposalId === null) return null;
        setSaveRefusal(undefined);

        if (!savedProposalId && !proposalId) {
          throw new Error("Saved proposal is missing an ID");
        }

        if (savedProposalId && !proposalId && !location.pathname.includes("/proposals/drafts/edit")) {
          const savedDraftPath = `/proposals/drafts/edit/${savedProposalId}/details`;
          appDirectedNavigationPathRef.current = savedDraftPath;
          navigate(savedDraftPath, { replace: true });
          dispatchPanelState({ type: "setSelectedReviewField" });
        }

        return savedProposalId;
      } catch (error) {
        console.error("Error saving proposal updates", error);
        setSaveRefusal(DraftSaveRefusal.fromError(error, updatedBuilderRef.current));
        if (reportProposalMissingItemSaveFailure(error, collaborationRef.current)) return;
        showErrorSnackbar(error, {
          doing: "saving your proposal changes",
          operation: "proposal.save-draft",
          message: documentRefusalMessage(error, updatedBuilderRef.current),
          staleMessage: PROPOSAL_CHANGED_MESSAGE,
        });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dispatchPanelState, location.pathname, proposalId],
  );

  /**
   * The marketplace team behind this proposal's vendor team, when there is one.
   *
   * Read once here rather than in the drawer's props, so the fee-schedule panel and anything else
   * that needs it cannot disagree about which team's profile is being offered.
   */
  const marketplaceVendorTeamId = getMarketplaceTeamId(
    updatedBuilder?.currentSpec.workAgreement?.vendorTeam,
  );

  const publishedRatesOfferTeam = publishedRatesOfferTeamId({
    viewingAsVendor: session.context?.viewingAsVendor,
    vendorTeam: updatedBuilder?.currentSpec.workAgreement?.vendorTeam,
    feeSchedule: updatedBuilder?.currentSpec.workAgreement?.feeSchedule,
    mayEdit: !isReadOnlyView && !pageControlsArePaused && !reviewCompletionLocksEditing && canEditProposal,
    isDismissed: publishedRatesOfferDismissed,
    isOnFeeScheduleTab: activeTab === PageTab.FeeSchedule,
  });

  function handlePublishedRatesOffered(schedule: PublishedFeeSchedule) {
    if (!updatedBuilder) return;
    void applyPublishedFeeScheduleToDraft({
      proposalBuilder: updatedBuilder,
      schedule,
      reconcileStaffing,
      // The staffing question waits on the user, and a collaborator's save can land meanwhile: finish
      // on the draft and the handler as they are then, never on this render's.
      getLatestProposalBuilder: () => updatedBuilderRef.current ?? undefined,
      onProposalBuilderUpdated: (proposalBuilder) => void handleProposalBuilderUpdatedRef.current(proposalBuilder),
    });
  }

  function handleCreateCategory() {
    dispatchPanelState({ type: "openAddFeePanel" });
  }

  function handleEditCategory(category: FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency) {
    dispatchPanelState({ type: "openAddFeePanel", category });
  }

  function handleAddExistingCategory() {
    dispatchPanelState({ type: "openExistingFeePanel" });
  }

  /**
   * A press on an issue, in a tab's list or in its badge's popover (#6097). It moves to the field, as
   * "Prev / Next Issue" would, and scrolls the grid to the cell the issue is about. An issue about a
   * rate's Team Member or Fee also opens that category's editor on that field, which is where the
   * reader fixes it — when the row's own Edit action would open it, and not otherwise.
   */
  function handleIssueSelected(issue: ProposalIssue) {
    handleTraverseToNewField(issue.field);
    revealFeeScheduleIssueCell(issue);
    const focus = issue.categoryEditorField;
    if (!focus || !issue.field.id || feeScheduleEditingIsDisabled) return;
    const feeSchedule = updatedBuilder?.currentSpec.workAgreement?.feeSchedule;
    if (!Masked.isPresentNotMasked(feeSchedule)) return;
    const category = feeSchedule.find((candidate) => candidate.id.isEqualTo(issue.field.id));
    if (!category) return;
    dispatchPanelState({ type: "openAddFeePanel", category, focus });
  }

  async function handleExistingCategoriesSelected(
    categories: Array<FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency>,
  ) {
    if (!updatedBuilder) {
      throw new Error("Proposal builder is required to add existing fee categories.");
    }

    const existingCategories = updatedBuilder.currentSpec.workAgreement?.feeSchedule ?? [];

    if (Masked.isMasked(existingCategories) || Masked.isMasked(categories)) {
      showActionableErrorSnackbar(HIDDEN_RATES_ADD_MESSAGE);
      dispatchPanelState({ type: "closeExistingFeePanel" });
      return;
    }

    let updatedCategories = existingCategories;
    const { duplicateCategories, uniqueCategories: distinctCategories } = FeeSchedule.splitCategoriesByDuplicate(
      existingCategories,
      categories,
    );
    // A rate is known by its name: one of the same name at another price is the same rate twice.
    const uniqueCategories = withoutRatesNamedOn(existingCategories, distinctCategories);

    if (duplicateCategories.length > 0 || uniqueCategories.length < distinctCategories.length) {
      enqueueSnackbar(SAME_NAME_CATEGORIES_SKIPPED, { variant: "warning" });
    }

    // A team's published rate names who bills it (#4920); a catalog category names nobody, so this
    // asks nothing for those. Anybody who cannot bill this proposal is put to the user, as a
    // template's people are (#5237), and dismissing the question adds nothing.
    const workAgreement = updatedBuilder.currentSpec.workAgreement;
    const vendorTeam = Masked.isPresentNotMasked(workAgreement?.vendorTeam) ? workAgreement.vendorTeam : undefined;
    const staffing = await reconcileStaffing({
      categories: uniqueCategories,
      teamMembers: getFeeScheduleTeamMembers(vendorTeam),
      teamIsMasked: Masked.isMasked(workAgreement?.vendorTeam),
      excludedLegalEntityId: FeeScheduleTeamRoster.fromVendorTeam(workAgreement?.vendorTeam, workAgreement?.clientTeam)
        .excludedLegalEntityId,
    });
    if (!staffing.applied) return;

    // The question waits on the user, and a collaborator's save can land meanwhile. Finish on the
    // draft as it stands now, or the whole proposal queued would carry that save back out (#4920).
    const latestBuilder = updatedBuilderRef.current ?? updatedBuilder;
    const latestWorkAgreement = latestBuilder.currentSpec.workAgreement;
    const latestCategories = Masked.isPresentNotMasked(latestWorkAgreement?.feeSchedule)
      ? latestWorkAgreement.feeSchedule
      : [];
    const latestVendorTeam = Masked.isPresentNotMasked(latestWorkAgreement?.vendorTeam)
      ? latestWorkAgreement.vendorTeam
      : undefined;
    // A collaborator may have added a rate of the same name meanwhile, at any price. Theirs stays.
    const categoriesToAdd = withoutRatesNamedOn(latestCategories, staffing.categories);
    if (categoriesToAdd.length < staffing.categories.length) {
      enqueueSnackbar(SAME_NAME_CATEGORIES_SKIPPED, { variant: "warning" });
    }
    updatedCategories = [...latestCategories, ...categoriesToAdd];

    // Only the people billing a rate that is added: a skipped rate seats nobody.
    const membersToSeat = staffingForRates(staffing.membersToAddToTeam, categoriesToAdd);
    let builderWithCategories = latestBuilder;
    if (membersToSeat.length > 0) {
      builderWithCategories = builderWithCategories.setVendorTeam(
        withFeeScheduleStaffingSeated(latestVendorTeam ?? new VendorTeam({ members: [] }), membersToSeat),
      );
    }
    dispatchPanelState({ type: "closeExistingFeePanel" });
    void handleProposalBuilderUpdatedRef.current(builderWithCategories.setFeeSchedule(updatedCategories));
  }

  function handleCategoryAdded(addedCategory: FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency) {
    if (!updatedBuilder) {
      throw new Error("Proposal builder is required to add a fee category.");
    }

    const selectedCategories = updatedBuilder?.currentSpec.workAgreement?.feeSchedule ?? [];

    if (Masked.isMasked(selectedCategories) || Masked.isMasked(addedCategory)) {
      showActionableErrorSnackbar(HIDDEN_RATES_ADD_MESSAGE);
      return;
    }

    if (selectedCategories.some((c) => c.isEqualTo(addedCategory))) {
      enqueueSnackbar("Fee category is already in the fee schedule.", { variant: "warning" });
      return;
    }

    const category = addedCategory;
    const staffedBuilder = withChosenTeamMemberSeated(updatedBuilder, category.teamMember, null);

    const updatedCategories = [
      ...selectedCategories,
      category instanceof FeeScheduleCategoryMaskedCurrency
        ? new FeeScheduleCategoryMaskedCurrency({
            id: Guid.generate(),
            name: category.name,
            description: category.description ?? null,
            type: category.type,
            teamMember: category.teamMember,
            teamMemberIsMasked: category.teamMemberIsMasked,
          })
        : new FeeScheduleCategory({
            name: category.name,
            description: category.description ?? null,
            fee: category.fee,
            type: category.type,
            id: Guid.generate(),
            teamMember: category.teamMember,
            teamMemberIsMasked: category.teamMemberIsMasked,
          }),
    ];

    handleProposalBuilderUpdated(staffedBuilder.setFeeSchedule(updatedCategories));
    setNewFeeCategoryFormKey(Guid.generate().value);
    dispatchPanelState({ type: "closeAddFeePanel", clearCategory: true });
  }

  function handleCategoryUpdated(
    originalName: FeeScheduleCategoryName | null,
    editedCategory: FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency,
  ) {
    if (originalName === null) return;

    if (!updatedBuilder) {
      throw new Error("Proposal builder is required to update a fee category.");
    }

    const selectedCategories = updatedBuilder?.currentSpec.workAgreement?.feeSchedule ?? [];

    if (Masked.isMasked(selectedCategories) || Masked.isMasked(editedCategory)) {
      showActionableErrorSnackbar(HIDDEN_RATES_UPDATE_MESSAGE);
      return;
    }

    if (selectedCategories.some((c) => c.isEqualTo(editedCategory))) {
      enqueueSnackbar("Fee category is already in the fee schedule.", { variant: "warning" });
      return;
    }

    const updatedCategory = editedCategory;
    const openedCategory = selectedCategories.find((category) => category.name?.isEqualTo(originalName));
    const staffedBuilder = withChosenTeamMemberSeated(
      updatedBuilder,
      updatedCategory.teamMember,
      openedCategory?.teamMember,
    );

    const updatedCategories = selectedCategories.map((category) => {
      if (category.name?.isEqualTo(originalName)) return updatedCategory;
      return category;
    });

    handleProposalBuilderUpdated(staffedBuilder.setFeeSchedule(updatedCategories));
    dispatchPanelState({ type: "closeAddFeePanel" });
  }
  // #endregion

  // #region Render
  const pageTitle = (() => {
    if (pageAction === "view") return "View";
    if (pageAction === "create") return "Create";
    if (pageAction === "review") return "Review Draft";
    if (pageAction === "edit") return "Edit Draft";
    return "View";
  })();
  const currentUserReviewer = reviewers.find((reviewer) => reviewer.userId?.isEqualTo?.(session.user?.id));
  // The fee schedule's rows in the order its table draws them, so Prev / Next walks down it as the
  // reader sorted it (#6617). While the table is not drawn, the schedule's own order, which is the
  // order the table opens in.
  const draftFeeSchedule = updatedBuilder?.currentSpec.workAgreement?.feeSchedule;
  const feeScheduleTraversalRowOrder = feeScheduleRowOrder(
    displayedFeeScheduleCategoryIds ??
      (Masked.isPresentNotMasked(draftFeeSchedule) ? draftFeeSchedule.map((category) => category.id) : []),
  );
  // Fields whose only pending item is an unread counterparty change, so the issue traverser stops there.
  const unreadChangeFields = proposalUnreadChangeFields(
    collaboration.mode === "canonical" ? collaboration.envelope : undefined,
    session.user?.id?.value,
  );
  const manageReviewersDisabled =
    !updatedBuilder ||
    pageControlsArePaused ||
    isSelectingClient ||
    isSelectingClient ||
    isManagingReviewers ||
    reviewerSelectorAnchor !== undefined ||
    isReadOnlyView ||
    isReviewing;
  const pendingReviewCount = hasPendingNonReviewerBuilderChanges
    ? reviewers.length
    : reviewers.filter((reviewer) => !reviewerHasCurrentApproval(reviewer)).length;
  // Comments are deliberately absent. The page used to count the actor's staged comments as unsaved
  // work, which armed the discard-on-navigation prompt over a conversation with nothing pending in it;
  // a comment is written when it is written, so leaving the page cannot lose one.
  let isDirty = hasPendingReviewerChanges;
  if (!proposal) {
    isDirty = isDirty || builderDiffersFromOriginal;
  }
  // A proposal whose source could not answer cannot be saved either — the editor is holding a partial
  // picture of it, and the page has no way to tell "empty" from "unread".
  const proposalSourceFailedToLoad = collaboration.mode === "error";
  const disableSaveChanges = !isDirty || !updatedBuilderIssues.canSave || proposalSourceFailedToLoad;
  const disableSave =
    pageControlsArePaused ||
    giveReviewerApprovalQueryResult.isLoading ||
    completeProposalReviewResult.isLoading ||
    isSelectingClient ||
    isManagingReviewers ||
    disableSaveChanges;
  const hasPendingDraftContentUpdates =
    collaboration.mode === "canonical"
      ? collaboration.hasPendingChanges
      : hasPendingNonReviewerBuilderChanges || (!proposal && builderDiffersFromOriginal);
  const saveButtonTooltip = getSaveButtonTooltip({
    hasPendingDraftContentUpdates,
    hasReviewerRecipients: reviewers.length > 0 && !isReviewing,
    isDirty,
    proposalIsSaved: Boolean(proposalId),
    proposalCanSave: updatedBuilderIssues.canSave,
    userCanEdit: Boolean(session.user?.canEdit(proposal)),
  });
  const drawerWidth = getProposalDrawerWidth({
    teamSelectionOpen: isTeamSelectionDrawerOpen,
    commentsOpen: commentsPanelIsOpen && commentsPaneIsAvailable,
    addFeeOpen: isAddFeePanelOpen,
    existingFeeOpen: isExistingFeePanelOpen,
    canSeatSidePanel,
  });
  const hasDraftBuilderToCleanUp = Boolean(
    originalBuilderJSON || updatedBuilderJSON || originalBuilder || updatedBuilder,
  );

  const handleNavigateBack = useCallback(
    async (continueNavigation: () => void) => {
      if (isDirty) {
        const response = await confirm({
          title: "Discard changes?",
          message: "Are you sure you want to discard changes to the proposal?",
          okButtonText: "Discard",
          alternativeOkText: disableSaveChanges ? undefined : "Save & Close",
          cancelButtonText: "Cancel",
        });

        if (response === ConfirmResponse.Cancel) return false;
        if (response === ConfirmResponse.AltOk) {
          const savedProposalId = await handleSaveUpdatesClicked({ confirmSave: false });
          if (!savedProposalId) return false;
        }
      }

      handleProposalDraftCleanup(proposalId);

      dispatchPanelState({ type: "setSelectedReviewField" });
      continueNavigation();
      return true;
    },
    [
      confirm,
      disableSaveChanges,
      handleProposalDraftCleanup,
      handleSaveUpdatesClicked,
      isDirty,
      dispatchPanelState,
      proposalId,
    ],
  );

  // The guard owns the blocked navigation from here: it asks `handleNavigateBack` what to do, and it
  // takes the user to the route they clicked even when the page navigates while the prompt is open —
  // which it does itself, because a new draft's first save replaces the `create` URL with the saved
  // draft's `edit` URL (#5545). Opening another draft, or a new one, in this editor leaves this
  // draft's edits behind, so it asks too (#7070).
  useUnsavedProposalNavigationGuard({
    shouldBlockNavigation: ({ currentLocation, nextLocation }) =>
      (isDirty || hasDraftBuilderToCleanUp) &&
      !isSubmittingProposalRef.current &&
      !isDeletingProposalRef.current &&
      navigationLeavesProposalEditor("drafts", currentLocation.pathname, nextLocation.pathname),
    onNavigateAway: handleNavigateBack,
    appDirectedNavigationPathRef,
  });
  // Reloading, closing the tab and typing an address never reach the router (#7070).
  useAskBeforeUnload(isDirty);

  if (!sessionPermissions) return permissionsLoadState;
  if (!sessionPermissions.includesAccessToProposals) return <NotAuthorized />;
  // Drafting needs Read/Write proposals access — see `User.canCreateProposals`, which also turns
  // away an officer whose firm is still onboarding. Refusing here rather than at Save is the point:
  // every route into a new draft (the Proposals page, the marketplace's Create Proposal, a pasted
  // URL) lands on this component, so this one gate stops anyone building a whole proposal the API
  // was always going to answer 403 to.
  if (!proposalId && !sessionUser?.canCreateProposals()) {
    return (
      <NotAuthorized message="You do not have permission to create a proposal. Ask an administrator at your firm for read/write proposal access." />
    );
  }

  // #region Secondary Actions
  /**
   * The actions that manage the draft rather than move it on, in the order they are offered.
   *
   * `ProposalSecondaryActions` draws them as buttons while the pane can seat them and collapses them
   * into one menu once it cannot, so Submit is never below a row of things that could have waited
   * (#5239). The active page describes its own set the same way, which is what keeps the two pages
   * from answering the same question differently.
   *
   * One literal with conditional spreads rather than a `push` per action: the delete handler guards
   * itself with a ref, and handing such a function to any call during render is what
   * `react-hooks/refs` warns about. Nothing here calls anything.
   */
  // A client's opening offer can go to several firms at once (#576). A revision of a hired agreement
  // has one counterparty already, and a vendor does not put a client's work out to bid.
  const canSendToSeveralFirms = showsSubmit && isOpeningOffer && !isViewingAsVendor && viewerOwnsDraft;
  const draftVendorTeam = updatedBuilder?.currentSpec.workAgreement?.vendorTeam;
  const firmsOnTheDraft: ProposalFirm[] =
    Masked.isPresentNotMasked(draftVendorTeam) && draftVendorTeam.leader
      ? [
          {
            leader: draftVendorTeam.leader,
            members: draftVendorTeam.members,
            marketplaceId: draftVendorTeam.marketplaceId,
            name: submitRecipient?.fullName ?? "The vendor team on this draft",
          },
        ]
      : [];

  // Not `canSubmit`: each firm gets its own representative in the dialog, so a draft with no vendor
  // team leader can still go to several firms (#6638). Single-firm Submit still needs one.
  const sendToSeveralFirmsDisabled =
    pageControlsArePaused ||
    isSelectingClient ||
    isManagingReviewers ||
    !issues?.canSendToSeveralFirms ||
    mayNotSendOpeningOffer;
  // Behind the arrow beside Submit, since sending to several firms is a way of submitting (#6455),
  // and handing the draft over is how a creator gets it submitted by somebody else (#6579).
  const sendToSeveralFirmsOptions: ProposalSubmitOption[] = canSendToSeveralFirms
    ? [
        {
          key: "send-to-several-firms",
          label: "Send to Several Firms",
          icon: <GroupAddIcon />,
          disabled: sendToSeveralFirmsDisabled,
          onClick: () => setIsFirmSendOpen(true),
        },
      ]
    : [];
  // And on the Vendor Team tab, where a client chooses between one firm and several (#6638).
  const askSeveralFirms = canSendToSeveralFirms
    ? {
        disabled: sendToSeveralFirmsDisabled,
        disabledReason: issues?.canSendToSeveralFirms ? undefined : "Fill out the required fields first.",
        onClick: () => setIsFirmSendOpen(true),
      }
    : undefined;
  const handOverOptions: ProposalSubmitOption[] = canHandOver
    ? [
        {
          key: "hand-over",
          label: `Hand Over to ${primaryNegotiatorName}`,
          description: "Team leader \u00b7 submits the draft",
          icon: <ShortcutIcon />,
          disabled: pageControlsArePaused || isDirty || handOverProposalDraftResult.isLoading,
          onClick: () => void handleHandOverClicked(),
        },
      ]
    : [];
  const submitOptions: ProposalSubmitOption[] = [...sendToSeveralFirmsOptions, ...handOverOptions];
  // The same hand-over, beside the leader it reaches on the team tab of the viewer's own side
  // (#6579). The label is the one a submitted proposal's leader change uses.
  const handOverLeaderActions: TeamLeaderRoleAction[] = canHandOver
    ? [
        {
          key: "hand-over-draft",
          label: "Hand Over",
          ariaLabel: `Hand this draft over to ${primaryNegotiatorName}`,
          disabled: pageControlsArePaused || isDirty || handOverProposalDraftResult.isLoading,
          onClick: () => void handleHandOverClicked(),
        },
      ]
    : [];

  const secondaryActions: ProposalSecondaryAction[] = [
    ...(!isReadOnlyView && !isReviewing
      ? [
          {
            key: "reviewers",
            label: "Reviewers",
            icon: <GroupsIcon />,
            badge:
              manageReviewersDisabled || !reviewers.length ? undefined : (
                <ReviewerApprovalBadge pendingReviewCount={pendingReviewCount} />
              ),
            loading: isLoading,
            disabled: isLoading || manageReviewersDisabled,
            onClick: handleManageReviewersClicked,
          },
        ]
      : []),
    // Offered where the server offers Delete, which is to the draft's owner alone: its creator until
    // they hand it over (#6579). The same rule decides which drafts the list lets a reader select for
    // deletion (#6577).
    ...(!isReadOnlyView && proposal?.isDeletableBy(session.user?.id)
      ? [
          {
            key: "delete-proposal",
            label: "Delete Proposal",
            icon: <DeleteIcon />,
            destructive: true,
            loading: isDeleting,
            disabled:
              pageControlsArePaused ||
              isSelectingClient ||
              isDeleting ||
              isSubmitting ||
              isSaving ||
              isManagingReviewers ||
              !proposal?.id ||
              isReviewing,
            onClick: handleDeleteClicked,
          },
        ]
      : []),
  ];

  return (
    <WorkPaneWidthContext.Provider value={paneWidth}>
    <ThemeProvider theme={paneTheme}>
    <PageContainer>
      <ProposalPageContent>
      <Main
        ref={paneRef}
        dimmed={isAddFeePanelOpen || isExistingFeePanelOpen || isTeamSelectionDrawerOpen}
        mobilePadding="compact"
        open={
          commentsPanelIsOpen ||
          isAddFeePanelOpen ||
          isExistingFeePanelOpen ||
          isTeamSelectionDrawerOpen
        }
      >
        <StickyPageHeader>
          <HeaderContainer>
            <TitleContainer>
              <PageTitle variant="h4" aria-label={breadcrumbHeadingName(["Proposals"], WorkVersion.nameWithVersion(pageTitle, WorkVersion.of(proposal?.workAgreement)))}>
                <PageTitleBreadcrumb label="Proposals" to="/proposals" />
                {pageTitle}
                <WorkVersionChip version={WorkVersion.of(proposal?.workAgreement)} />
              </PageTitle>
            </TitleContainer>
            <ActionButtonsContainer>
              <IssueTrackerHeader>
                <IssueTraverser
                  disabled={pageControlsArePaused}
                  rowOrder={feeScheduleTraversalRowOrder}
                  issueFields={issueFields}
                  unreadChangeFields={unreadChangeFields}
                  currentField={activeReviewField}
                  lastStop={traversalStop}
                  unreadCommentCounts={unreadCommentCounts}
                  stopName={ISSUE_TRAVERSER_STOP_NAME}
                  onTraverseToNewField={handleTraverseFromTraverser}
                  onTraverseToUnreadComments={commentsPaneIsAvailable ? handleTraverseToUnreadComments : undefined}
                />
              </IssueTrackerHeader>
              <ProposalSaveSplitButton
                compact={paneIsNarrow}
                disabled={disableSave}
                loading={isSaving}
                tooltip={saveButtonTooltip}
                options={saveAsProposalTemplate.actions.map((action) => ({
                  key: action.key,
                  label: action.label,
                  icon: action.icon,
                  disabled: action.disabled || pageControlsArePaused || isSaving || isSubmitting || isDeleting,
                  onClick: saveAsProposalTemplate.open,
                }))}
                onSave={() =>
                  handleSaveUpdatesClicked({
                    confirmSave: true,
                    confirmationMode: isReviewing ? "review" : "save",
                  })
                }
              />
              <CommentButton>
                <Tooltip
                  enterDelay={600}
                  placement="bottom-end"
                  title={proposal?.id ? "" : "Save the proposal to add comments."}
                >
                  <ButtonContainer>
                    <HighlightCommentButton
                      // The tooltip is the button's accessible name only while it has one; a saved
                      // proposal suppresses it, and the visible "Comments" label below is a
                      // sibling of the button, not its content. Name the icon button itself so it
                      // keeps that name in both states.
                      aria-label={proposalCommentsTitle}
                      active={activeReviewField?.isEqualTo(ProposalField.General)}
                      size="medium"
                      color="primary"
                      // A save in flight does not touch the conversation, so it leaves Comments open.
                      disabled={createdProposalRouteIsTransitioning || !proposal?.id}
                      onClick={handleOpenGeneralCommentsPanel}
                    >
                      <Badge
                        variant={unreadCommentCount ? "standard" : "dot"}
                        color="secondary"
                        overlap="circular"
                        badgeContent={unreadCommentCount > 0 ? unreadCommentCount : undefined}
                        invisible={unreadCommentCount === 0}
                      >
                        <CommentIcon fontSize="medium" />
                      </Badge>
                    </HighlightCommentButton>
                    <CommentButtonLabel
                      variant="button"
                      color="primary"
                      disabled={createdProposalRouteIsTransitioning || !proposal?.id}
                    >
                      {proposalCommentsTitle}
                    </CommentButtonLabel>
                  </ButtonContainer>
                </Tooltip>
              </CommentButton>
              <CommentButton>
                <ButtonContainer>
                  <HighlightCommentButton
                    // The visible caption is a sibling of the button, not its content.
                    aria-label={proposalActivityTitle}
                    aria-expanded={Boolean(activityAnchor)}
                    active={Boolean(activityAnchor)}
                    size="medium"
                    color="primary"
                    // An unsaved draft has no negotiation yet, and so no activity to read.
                    disabled={!collaboration.envelope?.negotiationId}
                    onClick={(event) => setActivityAnchor(activityAnchor ? null : event.currentTarget)}
                  >
                    <HistoryIcon fontSize="medium" />
                  </HighlightCommentButton>
                  <CommentButtonLabel
                    variant="button"
                    color="primary"
                    disabled={!collaboration.envelope?.negotiationId}
                  >
                    {proposalActivityTitle}
                  </CommentButtonLabel>
                </ButtonContainer>
              </CommentButton>
              {/* Who holds a grant on the proposal, for an administrator with Member Permissions (#6172). */}
              {proposalAccess.mayOpen && proposal?.id && (
                <RecordAccessButton onClick={() => { if (proposal?.id) proposalAccess.open(proposal.id); }} />
              )}
              <ProposalActivityPopover
                anchorEl={activityAnchor}
                onClose={() => setActivityAnchor(null)}
                negotiationId={collaboration.envelope?.negotiationId}
                viewerScope={collaboration.viewerScope}
                coverage={activityCoverage}
              />
            </ActionButtonsContainer>
          </HeaderContainer>
          {updatedBuilder && (
            <>
              <PageActionsContainer>
                <ProposalActions>
                  {isReviewing && (
                    <Tooltip enterDelay={600} placement="bottom" title={submitReviewTooltip}>
                      <span>
                        <SubmitReviewButton
                          disabled={
                            pageControlsArePaused ||
                            giveReviewerApprovalQueryResult.isLoading ||
                            completeProposalReviewResult.isLoading ||
                            isSelectingClient ||
                            isManagingReviewers
                          }
                          size={paneIsNarrow ? "small" : "medium"}
                          finalized={hasUserApprovedReview}
                          loading={
                            isSaving || giveReviewerApprovalQueryResult.isLoading || completeProposalReviewResult.isLoading
                          }
                          canEdit={canEditProposal}
                          hasPendingChanges={hasPendingReviewerChanges}
                          pendingChangeSummary={{
                            heading: proposalSaveChangeLogHeading({
                              audienceLabel: reviewCompletionSharePlan.audienceLabel,
                            }),
                            descriptors: reviewCompletionSharePlan.descriptors,
                          }}
                          onFinalize={handleFinalizeReviewClicked}
                          onSubmitAllAndFinalize={handleSubmitAllAndFinalizeReviewClicked}
                        />
                      </span>
                    </Tooltip>
                  )}
                  {isReviewing && <ReviewerLeaderMessage message={currentUserReviewer?.customMessage} />}
                  {showsSubmit && (
                    <ProposalActionSpan>
                      <ProposalSubmitSplitButton
                        compact={paneIsNarrow}
                        tooltip={
                          mayNotSendOpeningOffer
                            ? canHandOver
                              ? `Sending a new proposal needs entity-wide proposal access. Hand this draft over to ${primaryNegotiatorName} from the menu beside Submit, and they can send it.`
                              : "Sending a new proposal needs entity-wide proposal access. Ask an administrator at your firm, or name your team leader on this draft and hand it over to them."
                            : issues?.canSubmit
                              ? "Submit proposal to other party."
                              : "Fill out required fields to submit."
                        }
                        startIcon={issues?.canSubmit ? <SendIcon /> : <WarningIcon />}
                        loading={isSubmitting}
                        disabled={
                          pageControlsArePaused ||
                          isSelectingClient ||
                          isManagingReviewers ||
                          !issues?.canSubmit ||
                          mayNotSendOpeningOffer ||
                          !showsSubmit
                        }
                        options={submitOptions}
                        onSubmit={handleSubmitClicked}
                      />
                    </ProposalActionSpan>
                  )}
                  {
                    // #region Secondary Actions
                  }
                  <ProposalSecondaryActions
                    actions={secondaryActions}
                    collapsed={paneIsNarrow}
                    size={paneIsNarrow ? "small" : "medium"}
                  />
                  {saveAsProposalTemplate.dialog}
                  {isFirmSendOpen && (
                    <SendToSeveralFirmsDialog
                      initialFirms={firmsOnTheDraft}
                      clientLegalEntityId={session.user?.legalEntityId}
                      responseDueBy={updatedBuilder?.currentSpec.responseDueBy}
                      onSend={handleSendToFirms}
                      onClose={handleFirmSendClosed}
                    />
                  )}
                </ProposalActions>
                {proposalAccess.drawer}
              </PageActionsContainer>
              {/* A popover, so it is not a cell of the actions grid; it hangs off whichever control
                  opened it — the Reviewers button, or "More Actions" once they have collapsed. */}
              {!isReadOnlyView && !isReviewing && (
                <ReviewerSelector
                  proposal={proposal ?? undefined}
                  popoverAnchor={reviewerSelectorAnchor}
                  reviewerSource="proposalBuilder"
                  reviewerBaseline={reviewerBaseline}
                  // The team the tabs draw, staged edits included: a saved draft queues them rather
                  // than storing a builder, and a vendor may ask someone it has just put on the team.
                  vendorTeam={updatedBuilder?.currentSpec.workAgreement.vendorTeam}
                  canRequestReReview={reReview.canRequestReReview}
                  reReviewPendingUserIds={reReview.pendingUserIds}
                  onRequestReReview={reReview.requestReReview}
                  // Only when somebody is pending: the popover insets whatever it is given (review of #6781).
                  pendingReviewerReminders={
                    collaboration.mode === "canonical" &&
                    collaboration.envelope &&
                    pendingProposalReviewers(collaboration.envelope, collaboration.pendingChanges.reviewerChanges)
                      .length > 0 ? (
                      <PendingReviewerReminders
                        envelope={collaboration.envelope}
                        viewerScope={collaboration.viewerScope}
                        stagedReviewerChanges={collaboration.pendingChanges.reviewerChanges}
                      />
                    ) : undefined
                  }
                  proposalHasUnsavedChanges={isDirty}
                  isSendingToReviewers={isSaving}
                  // #4842: the header Save is disabled while the popover is open, so the
                  // popover carries its own way to persist the roster; it closes first so
                  // the confirmation is not stacked behind it. #5134: Send is no longer
                  // gated on this page being dirty, so it asks first only when it is —
                  // when it would flush proposal edits the actor did not press Save for.
                  onSendToReviewers={({ confirmSave }) => {
                    dispatchPanelState({ type: "closeReviewerSelector" });
                    void handleSaveUpdatesClicked({ confirmSave, confirmationMode: "save" });
                  }}
                  onPopoverClose={() => {
                    dispatchPanelState({ type: "closeReviewerSelector" });
                  }}
                  onReviewersChanged={(updatedReviewers) => {
                    if (!session.context) throw new Error("Session not properly registered.");
                    const latestBuilder = updatedBuilderRef.current;
                    if (!latestBuilder)
                      throw new Error("Proposal builder not registered properly in store.");
                    void handleProposalBuilderUpdated(
                      session.context?.viewingAsVendor
                        ? latestBuilder.setVendorReviewers(updatedReviewers)
                        : latestBuilder.setClientReviewers(updatedReviewers),
                    );
                  }}
                />
              )}
            </>
          )}
          {updatedBuilder && viewerHandedDraftOver && (
            <Alert severity="info" sx={{ mx: 2, my: 1 }}>
              {`You handed this draft over to ${primaryNegotiatorName}, who owns it and submits it. You can keep working on it as a reviewer.`}
            </Alert>
          )}
          {updatedBuilder && (
            <ProposalTabsBand>
              <TabsContainer
                variant="scrollable"
                scrollButtons="auto"
                indicatorColor="primary"
                textColor="primary"
                allowScrollButtonsMobile
                value={activeTab}
                onChange={handleTabChange}
              >
                <ProposalTab
                  value={PageTab.Details}
                  disabled={shouldDisableTabs()}
                  label={
                    <ProposalIssuesBadge
                      issues={issues}
                      category={ProposalFieldCategory.Details}
                      envelope={collaboration.mode === "canonical" ? collaboration.envelope : undefined}
                      currentUserId={session.user?.id?.value}
                      onIssueSelected={handleIssueSelected}
                      {...tabPendingCounts.forCategory(ProposalFieldCategory.Details)}
                    />
                  }
                />
                <ProposalTab
                  value={PageTab.ClientTeam}
                  disabled={shouldDisableTabs()}
                  label="Client Team"
                />
                <ProposalTab
                  value={PageTab.VendorTeam}
                  disabled={shouldDisableTabs()}
                  label={
                    <ProposalIssuesBadge
                      title="Vendor Team"
                      issues={issues}
                      category={ProposalFieldCategory.Team}
                      envelope={collaboration.mode === "canonical" ? collaboration.envelope : undefined}
                      currentUserId={session.user?.id?.value}
                      onIssueSelected={handleIssueSelected}
                      {...tabPendingCounts.forCategory(ProposalFieldCategory.Team)}
                    />
                  }
                />
                <ProposalTab
                  value={PageTab.FeeSchedule}
                  disabled={shouldDisableTabs()}
                  label={
                    <ProposalIssuesBadge
                      issues={issues}
                      category={ProposalFieldCategory.FeeSchedule}
                      envelope={collaboration.mode === "canonical" ? collaboration.envelope : undefined}
                      currentUserId={session.user?.id?.value}
                      onIssueSelected={handleIssueSelected}
                      {...tabPendingCounts.forCategory(ProposalFieldCategory.FeeSchedule)}
                    />
                  }
                />
                <ProposalTab
                  value={PageTab.Conflicts}
                  disabled={shouldDisableTabs()}
                  label={
                    <ProposalIssuesBadge
                      issues={issues}
                      category={ProposalFieldCategory.Conflicts}
                      envelope={collaboration.mode === "canonical" ? collaboration.envelope : undefined}
                      currentUserId={session.user?.id?.value}
                      onIssueSelected={handleIssueSelected}
                      {...tabPendingCounts.forCategory(ProposalFieldCategory.Conflicts)}
                    />
                  }
                />
                <ProposalTab
                  value={PageTab.Policies}
                  disabled={shouldDisableTabs()}
                  label={
                    <ProposalIssuesBadge
                      issues={issues}
                      category={ProposalFieldCategory.Policies}
                      envelope={collaboration.mode === "canonical" ? collaboration.envelope : undefined}
                      currentUserId={session.user?.id?.value}
                      onIssueSelected={handleIssueSelected}
                      {...tabPendingCounts.forCategory(ProposalFieldCategory.Policies)}
                    />
                  }
                />
                <ProposalTab
                  value={PageTab.Discount}
                  disabled={shouldDisableTabs()}
                  label={
                    <ProposalIssuesBadge
                      issues={issues}
                      category={ProposalFieldCategory.Discount}
                      envelope={collaboration.mode === "canonical" ? collaboration.envelope : undefined}
                      currentUserId={session.user?.id?.value}
                      onIssueSelected={handleIssueSelected}
                      {...tabPendingCounts.forCategory(ProposalFieldCategory.Discount)}
                    />
                  }
                />
              </TabsContainer>
            </ProposalTabsBand>
          )}
        </StickyPageHeader>
        {!updatedBuilder && <NoBuilderLoader />}
        {updatedBuilder && (
          <Content {...paneFlushContent}>
          {/* Lets the change traverser open a field's history where the field lives — inside a tab. */}
          <ProposalFieldHistoryRequestContext.Provider value={fieldHistoryRequest}>
            <TraverserStopScopeContext.Provider value={traverserStopScope}>
            <TabContent ref={attachTraverserStopScope} disableBottomPadding={activeTab === PageTab.FeeSchedule}>
              {updatedBuilder && (
                <>
                    {publishedRatesOfferTeam && (
                      <PublishedRatesOffer
                        marketplaceTeamId={publishedRatesOfferTeam}
                        onApply={handlePublishedRatesOffered}
                        onDismiss={() => setPublishedRatesOfferDismissed(true)}
                      />
                    )}
                    {activeTab === PageTab.Details && (
                      <DetailsTab
                        proposal={proposal ?? undefined}
                        isSaved={Boolean(proposal?.id)}
                        savedFields={savedDraftFields}
                        issues={issues}
                        activeReviewField={activeReviewField}
                        proposalBuilder={updatedBuilder}
                        showPendingChangeHighlights={false}
                        disableEditing={Boolean(
                          isReadOnlyView ||
                          pageControlsArePaused ||
                          reviewCompletionLocksEditing ||
                          !canEditProposal,
                        )}
                        historyReadOnlyReason={proposalFieldRestoreLockReason({
                          cannotEdit: !canEditProposal,
                          reviewCompleted: reviewCompletionLocksEditing,
                          isViewOnly: isReadOnlyView,
                          isPaused: pageControlsArePaused,
                        })}
                        negotiationId={collaboration.envelope?.negotiationId}
                        viewerScope={collaboration.viewerScope}
                        onSelectingClientChange={(isSelecting: boolean) => setIsSelectingClient(isSelecting)}
                        onProposalBuilderUpdated={handleProposalBuilderUpdated}
                      />
                    )}
                    {activeTab === PageTab.ClientTeam && (
                      <ClientTeamTab
                        proposal={proposal ?? undefined}
                        proposalBuilder={updatedBuilder}
                        negotiationId={collaboration.envelope?.negotiationId}
                        canEdit={
                          proposal?.id
                            ? collaboration.envelope?.capabilities.canManageClientTeam
                            : Boolean(
                              !isReadOnlyView &&
                              !pageControlsArePaused &&
                              !reviewCompletionLocksEditing &&
                              canEditProposal,
                            )
                        }
                        leaderActions={isViewingAsVendor ? undefined : handOverLeaderActions}
                        onProposalBuilderUpdated={handleProposalBuilderUpdated}
                      />
                    )}
                    {activeTab === PageTab.VendorTeam && (
                      <TeamTab
                        negotiationId={collaboration.envelope?.negotiationId}
                        viewerScope={collaboration.viewerScope}
                        proposalBuilder={updatedBuilder}
                        showPendingChangeHighlights={false}
                        issues={issues}
                        disableEditing={Boolean(
                          isReadOnlyView ||
                          pageControlsArePaused ||
                          reviewCompletionLocksEditing ||
                          !canEditProposal,
                        )}
                        canChangeTeamIdentity={canChangeVendorTeamIdentity}
                        savedFields={savedDraftFields}
                        savedCollections={savedDraftCollections}
                        leaderActions={isViewingAsVendor ? handOverLeaderActions : undefined}
                        isDrawerOpen={commentsPanelIsOpen || isTeamSelectionDrawerOpen}
                        activeReviewField={activeReviewField}
                        onProposalBuilderUpdated={handleProposalBuilderUpdated}
                        onSelectionDrawerOpenChange={handleTeamSelectionDrawerOpenChange}
                        onTraverseToNewField={handleTraverseToNewField}
                        askSeveralFirms={askSeveralFirms}
                      />
                    )}
                    {activeTab === PageTab.FeeSchedule && (
                      <FeeScheduleTab
                        onDisplayedCategoriesChange={setDisplayedFeeScheduleCategoryIds}
                        savedCollections={savedDraftCollections}
                        negotiationId={collaboration.envelope?.negotiationId}
                        viewerScope={collaboration.viewerScope}
                        proposalBuilder={updatedBuilder}
                        showPendingChangeHighlights={false}
                        issues={issues}
                        isDrawerOpen={isAddFeePanelOpen || isExistingFeePanelOpen || commentsPanelIsOpen}
                        disableEditing={Boolean(
                          isReadOnlyView ||
                          pageControlsArePaused ||
                          reviewCompletionLocksEditing ||
                          !canEditProposal,
                        )}
                        activeReviewField={activeReviewField}
                        onProposalBuilderUpdated={handleProposalBuilderUpdated}
                        onCreateCategoryClicked={handleCreateCategory}
                        onAddExistingCategoryClicked={handleAddExistingCategory}
                        onEditCategory={handleEditCategory}
                        onIssueSelected={handleIssueSelected}
                        onTraverseToNewField={handleTraverseToNewField}
                      />
                    )}
                    {activeTab === PageTab.Conflicts && (
                      <ConflictsTab
                        negotiationId={collaboration.envelope?.negotiationId}
                        viewerScope={collaboration.viewerScope}
                        proposalBuilder={updatedBuilder}
                        proposalId={proposal?.id}
                        showPendingChangeHighlights={false}
                        savedFields={savedDraftFields}
                        savedCollections={savedDraftCollections}
                        issues={issues}
                        isDrawerOpen={commentsPanelIsOpen}
                        disableEditing={Boolean(
                          isReadOnlyView ||
                          pageControlsArePaused ||
                          reviewCompletionLocksEditing ||
                          !canEditProposal,
                        )}
                        activeReviewField={activeReviewField}
                        onProposalBuilderUpdated={handleProposalBuilderUpdated}
                        onTraverseToNewField={handleTraverseToNewField}
                      />
                    )}
                    {activeTab === PageTab.Policies && (
                      <PoliciesTab
                        savedCollections={savedDraftCollections}
                        negotiationId={collaboration.envelope?.negotiationId}
                        viewerScope={collaboration.viewerScope}
                        proposalBuilder={updatedBuilder}
                        proposalId={proposal?.id}
                        showPendingChangeHighlights={false}
                        issues={issues}
                        isDrawerOpen={commentsPanelIsOpen}
                        disableEditing={Boolean(
                          isReadOnlyView ||
                          pageControlsArePaused ||
                          reviewCompletionLocksEditing ||
                          !canEditProposal,
                        )}
                        activeReviewField={activeReviewField}
                        onProposalBuilderUpdated={handleProposalBuilderUpdated}
                        onTraverseToNewField={handleTraverseToNewField}
                      />
                    )}
                    {activeTab === PageTab.Discount && (
                      <DiscountTab
                        negotiationId={collaboration.envelope?.negotiationId}
                        viewerScope={collaboration.viewerScope}
                        proposalBuilder={updatedBuilder}
                        showPendingChangeHighlights={false}
                        savedFields={savedDraftFields}
                        issues={issues}
                        disableEditing={Boolean(
                          isReadOnlyView ||
                          pageControlsArePaused ||
                          reviewCompletionLocksEditing ||
                          !canEditProposal,
                        )}
                        activeReviewField={activeReviewField}
                        onProposalBuilderUpdated={handleProposalBuilderUpdated}
                      />
                    )}
                </>
              )}
            </TabContent>
            </TraverserStopScopeContext.Provider>
          </ProposalFieldHistoryRequestContext.Provider>
          </Content>
        )}
        <IssueTrackerFooter>
          <IssueTraverser
            disabled={pageControlsArePaused}
            rowOrder={feeScheduleTraversalRowOrder}
            issueFields={issueFields}
            unreadChangeFields={unreadChangeFields}
            currentField={activeReviewField}
            lastStop={traversalStop}
            unreadCommentCounts={unreadCommentCounts}
            stopName={ISSUE_TRAVERSER_STOP_NAME}
            onTraverseToNewField={handleTraverseFromTraverser}
            onTraverseToUnreadComments={commentsPaneIsAvailable ? handleTraverseToUnreadComments : undefined}
          />
        </IssueTrackerFooter>
      </Main>
      </ProposalPageContent>
      <SidePanelColumn
        data-testid="proposal-drawers"
        style={{
          width: drawerWidth,
        }}
      >
        {commentsPaneIsAvailable && (
          <CommentsDrawer
            proposal={commentProposal ?? proposal}
            commentThreads={commentThreads}
            isOpen={commentsPanelIsOpen}
            isSaving={isSaving}
            title={commentsPanelTitle}
            commentToScrollTo={commentToScrollTo}
            conversationToOpen={traversalStop?.kind === "unreadComments" ? traversalStop : undefined}
            forumRequest={commentsForumRequest}
            onOpen={handleOpenCommentsPanel}
            onClose={handleCloseCommentsPanel}
          />
        )}
        <EditFeeScheduleCategoryDrawer
          key={currentCategory?.id.value ?? newFeeCategoryFormKey}
          category={currentCategory}
          formKey={newFeeCategoryFormKey}
          isOpen={isAddFeePanelOpen}
          onOpen={() => dispatchPanelState({ type: "openAddFeePanel", preserveCategory: true })}
          onClose={() => {
            dispatchPanelState({ type: "closeAddFeePanel", clearCategory: true });
          }}
          onCategoryAdded={handleCategoryAdded}
          onCategoryUpdated={handleCategoryUpdated}
          selectedCategories={updatedBuilder?.currentSpec.workAgreement?.feeSchedule ?? []}
          offersTeamMember
          focus={panelState.categoryEditorFocus}
          teamRoster={FeeScheduleTeamRoster.fromVendorTeam(
            updatedBuilder?.currentSpec.workAgreement?.vendorTeam,
            updatedBuilder?.currentSpec.workAgreement?.clientTeam,
          )}
        />
        <ExistingFeeScheduleCategoryDrawer
          selectedCategories={updatedBuilder?.currentSpec.workAgreement?.feeSchedule ?? []}
          isOpen={isExistingFeePanelOpen}
          onOpen={() => dispatchPanelState({ type: "openExistingFeePanel" })}
          onClose={() => dispatchPanelState({ type: "closeExistingFeePanel" })}
          onCategoriesSelected={handleExistingCategoriesSelected}
          proposalId={negotiationGuid?.toString()}
          // What the vendor team advertises, offered as a second source (#5113). Undefined when the
          // team did not come from the marketplace, or when the team is masked to this viewer.
          marketplaceTeamId={marketplaceVendorTeamId}
        />
        {submitProposalDialog}
      </SidePanelColumn>
    </PageContainer>
    </ThemeProvider>
    </WorkPaneWidthContext.Provider>
  );
}

/** Said when picked categories are left out because the fee schedule has rates of their names. */
const SAME_NAME_CATEGORIES_SKIPPED =
  "Some categories were not added because categories with the same name already exist in the fee schedule.";

function proposalFieldCategoryFromPageTab(pageTab: PageTab): ProposalFieldCategoryType {
  switch (pageTab) {
    case PageTab.Details:
      return ProposalFieldCategory.Details;
    case PageTab.VendorTeam:
      return ProposalFieldCategory.Team;
    case PageTab.ClientTeam:
      return ProposalFieldCategory.Details;
    case PageTab.FeeSchedule:
      return ProposalFieldCategory.FeeSchedule;
    case PageTab.Conflicts:
      return ProposalFieldCategory.Conflicts;
    case PageTab.Policies:
      return ProposalFieldCategory.Policies;
    case PageTab.Discount:
      return ProposalFieldCategory.Discount;
  }
}

function pageTabFromProposalFieldCategory(proposalFieldCategory: ProposalFieldCategoryType): PageTab {
  switch (proposalFieldCategory) {
    case ProposalFieldCategory.Details:
      return PageTab.Details;
    case ProposalFieldCategory.Team:
      return PageTab.VendorTeam;
    case ProposalFieldCategory.FeeSchedule:
      return PageTab.FeeSchedule;
    case ProposalFieldCategory.Conflicts:
      return PageTab.Conflicts;
    case ProposalFieldCategory.Policies:
      return PageTab.Policies;
    case ProposalFieldCategory.Discount:
      return PageTab.Discount;
  }
}

function getDraftBuilderForUpdate(args: {
  originalBuilder?: ProposalBuilder | null;
  proposal?: Proposal | null;
  updatedBuilder: ProposalBuilder;
}) {
  const { originalBuilder, proposal, updatedBuilder } = args;

  if (!originalBuilder) return updatedBuilder;
  if (proposal) {
    return proposal.isModifiedByBuilder(updatedBuilder) ? updatedBuilder : originalBuilder;
  }

  return updatedBuilder.isDifferentFrom(originalBuilder) ? updatedBuilder : originalBuilder;
}

function proposalIsModifiedAgainstBaseline(proposal: Proposal, baselineProposal: Proposal): boolean {
  const proposalLike = proposal as Proposal & {
    isModified?: boolean;
    isModifiedAgainst?: (baselineProposal: Proposal) => boolean;
  };

  if (typeof proposalLike.isModifiedAgainst === "function") {
    return proposalLike.isModifiedAgainst(baselineProposal);
  }

  return Boolean(proposalLike.isModified);
}

/**
 * What Save promises, in the author's terms. It used to promise comments too — "Save changes and
 * comments", "Publish your pending comments" — because a save carried them; comments are written as
 * they are made, so Save speaks only for the proposal's own changes.
 */
function getSaveButtonTooltip(args: {
  hasPendingDraftContentUpdates: boolean;
  hasReviewerRecipients: boolean;
  isDirty: boolean;
  proposalCanSave: boolean;
  proposalIsSaved: boolean;
  userCanEdit: boolean;
}) {
  const { hasPendingDraftContentUpdates, hasReviewerRecipients, isDirty, proposalCanSave, proposalIsSaved } =
    args;

  if (!isDirty) return "No changes to save.";

  if (!proposalIsSaved) return "Save proposal.";
  if (!proposalCanSave) return "Fill out all required fields to save.";
  if (!hasPendingDraftContentUpdates) return "Save reviewer changes.";
  if (hasReviewerRecipients) return "Save changes. Reviewers will receive the updates.";

  return "Save changes.";
}
